import React, { useState, useEffect, useCallback } from "react";
import logoImage from '../images/Logo-Base-basic.png';
import Sidebar from '../components/SideBar';
import './dimensionRequests.css';
import { ENDPOINT_PATH } from '../config';

const AdminNavbar = () => {
    return (
        <div className="navWrapper">
            <div className="AdminNav">
                <div>
                    <a href="/" className="NavLink">
                        <div className="AdminLogo d-flex justify-content-end">
                            <img src={logoImage} alt='logo' className="NavLogo" />
                        </div>
                        <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
                    </a>
                </div>
            </div>
        </div>
    );
};

// Frame preview component - FINAL with image filling frame using background cover
const FramePreview = ({ product }) => {
    const frameSize = product.frameSize || product.imageDimensions || { width: 100, height: 100 };
    const imageSize = product.imageDimensions || { width: 100, height: 100 };
    const thickness = product.frameThickness || 1;
    const orientation = product.frameOrientation || 'portrait';
    const matboardNum = product.matboardNum || 0;
    const matboardThickness = product.matboardThickness || 0;
    const selectedMatboards = product.selectedMatboards || [];
    
    const containerWidth = 200;
    const containerHeight = 200;
    const isLandscape = orientation === 'landscape';
    
    // Frame dimensions from data (already in correct orientation)
    const frameWidth = frameSize.width;
    const frameHeight = frameSize.height;
    
    // Calculate scale to fit container
    const scale = Math.min(
        (containerWidth - 40) / (frameWidth + thickness * 2),
        (containerHeight - 40) / (frameHeight + thickness * 2)
    );
    
    // Scaled dimensions
    const scaledFrameWidth = Math.round((frameWidth + thickness * 2) * scale);
    const scaledFrameHeight = Math.round((frameHeight + thickness * 2) * scale);
    // Calculate image container size (accounting for matboards)
    const imageContainerWidth = Math.round((frameWidth - (matboardNum > 0 ? matboardThickness * 2 : 0)) * scale);
    const imageContainerHeight = Math.round((frameHeight - (matboardNum > 0 ? matboardThickness * 2 : 0)) * scale);
    const scaledThickness = Math.round(thickness * scale);
    const scaledMatboardThickness = matboardThickness * scale;
    
    // When landscape, swap image container dimensions since we're rotating the image back
    const finalImageWidth = isLandscape ? imageContainerHeight : imageContainerWidth;
    const finalImageHeight = isLandscape ? imageContainerWidth : imageContainerHeight;
    
    return (
        <div 
            className="frame-preview-container" 
            style={{ 
                width: containerWidth, 
                height: containerHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {/* Rotation wrapper - rotates frame and matboards for landscape */}
            <div style={{
                transform: isLandscape ? 'rotate(-90deg)' : 'none',
                transformOrigin: 'center center'
            }}>
                {/* Using table-based layout */}
                <table 
                    cellPadding="0" 
                    cellSpacing="0" 
                    style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}
                >
                    <tbody>
                        <tr>
                            <td style={{ padding: '20px' }}>
                                {/* Frame layer - using background image */}
                                <table 
                                    cellPadding="0" 
                                    cellSpacing="0" 
                                    style={{
                                        width: `${scaledFrameWidth}px`,
                                        height: `${scaledFrameHeight}px`,
                                        backgroundImage: product.productImageURL ? `url(${product.productImageURL})` : 'none',
                                        backgroundSize: `${scaledFrameWidth}px ${scaledFrameHeight}px`,
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                >
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: `${scaledThickness}px` }}>
                                                {/* Matboard layers */}
                                                {matboardNum > 0 && selectedMatboards.length > 0 ? (
                                                    <MatboardLayers 
                                                        matboardNum={matboardNum}
                                                        selectedMatboards={selectedMatboards}
                                                        scaledFrameWidth={scaledFrameWidth}
                                                        scaledFrameHeight={scaledFrameHeight}
                                                        scaledThickness={scaledThickness}
                                                        scaledMatboardThickness={scaledMatboardThickness}
                                                        imageContainerWidth={imageContainerWidth}
                                                        imageContainerHeight={imageContainerHeight}
                                                        imageURL={product.imageURL}
                                                        isLandscape={isLandscape}
                                                    />
                                                ) : (
                                                    /* No matboards - just image container with background cover */
                                                    product.imageURL && (
                                                        <div
                                                            style={{
                                                                width: `${finalImageWidth}px`,
                                                                height: `${finalImageHeight}px`,
                                                                backgroundImage: `url("${product.imageURL}")`,
                                                                backgroundSize: 'cover',
                                                                backgroundRepeat: 'no-repeat',
                                                                backgroundPosition: 'center',
                                                                transform: isLandscape ? 'rotate(90deg)' : 'none'
                                                            }}
                                                        />
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Helper component to render matboard layers with EQUAL SPACING and COVER images
const MatboardLayers = ({ 
    matboardNum, 
    selectedMatboards, 
    scaledFrameWidth, 
    scaledFrameHeight, 
    scaledThickness, 
    scaledMatboardThickness,
    imageContainerWidth,
    imageContainerHeight,
    imageURL,
    isLandscape
}) => {
    const innerWidth = scaledFrameWidth - (scaledThickness * 2);
    const innerHeight = scaledFrameHeight - (scaledThickness * 2);
    
    // Matboard reveal is 0.25 inches between layers (not based on matboard thickness)
    // This is a fixed 0.25" spacing between each matboard layer
    const matboardReveal = scaledMatboardThickness / 4;
    
    // Calculate matboard dimensions
    const layer1Width = innerWidth;
    const layer1Height = innerHeight;
    const layer2Width = innerWidth - (matboardReveal * 2);
    const layer2Height = innerHeight - (matboardReveal * 2);
    const layer3Width = innerWidth - (matboardReveal * 4);
    const layer3Height = innerHeight - (matboardReveal * 4);
    
    // When landscape, swap image container dimensions since we're rotating the image back
    const finalImageWidth = isLandscape ? imageContainerHeight : imageContainerWidth;
    const finalImageHeight = isLandscape ? imageContainerWidth : imageContainerHeight;
    
    if (matboardNum === 1) {
        return (
            <div style={{
                width: `${layer1Width}px`,
                height: `${layer1Height}px`,
                backgroundColor: selectedMatboards[0].hexColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {imageURL && (
                    <div
                        style={{
                            width: `${finalImageWidth}px`,
                            height: `${finalImageHeight}px`,
                            backgroundImage: `url("${imageURL}")`,
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            transform: isLandscape ? 'rotate(90deg)' : 'none'
                        }}
                    />
                )}
            </div>
        );
    }
    
    if (matboardNum === 2) {
        return (
            <div style={{
                width: `${layer1Width}px`,
                height: `${layer1Height}px`,
                backgroundColor: selectedMatboards[1].hexColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    width: `${layer2Width}px`,
                    height: `${layer2Height}px`,
                    backgroundColor: selectedMatboards[0].hexColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {imageURL && (
                        <div
                            style={{
                                width: `${finalImageWidth}px`,
                                height: `${finalImageHeight}px`,
                                backgroundImage: `url("${imageURL}")`,
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                transform: isLandscape ? 'rotate(90deg)' : 'none'
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }
    
    if (matboardNum === 3) {
        return (
            <div style={{
                width: `${layer1Width}px`,
                height: `${layer1Height}px`,
                backgroundColor: selectedMatboards[2].hexColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    width: `${layer2Width}px`,
                    height: `${layer2Height}px`,
                    backgroundColor: selectedMatboards[1].hexColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        width: `${layer3Width}px`,
                        height: `${layer3Height}px`,
                        backgroundColor: selectedMatboards[0].hexColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {imageURL && (
                            <div
                                style={{
                                    width: `${finalImageWidth}px`,
                                    height: `${finalImageHeight}px`,
                                    backgroundImage: `url("${imageURL}")`,
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    transform: isLandscape ? 'rotate(90deg)' : 'none'
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    return null;
};


const DimensionRequests = () => {
    // Page state
    const [currentPage, setCurrentPage] = useState('list'); // 'list', 'edit', 'view'
    const [currentOrderID, setCurrentOrderID] = useState(null);
    const [orderInfo, setOrderInfo] = useState(null);
    const [userType, setUserType] = useState(null);
    
    // List page state
    const [filterType, setFilterType] = useState('all');
    const [searchItem, setSearchItem] = useState('all-fields');
    const [searchInput, setSearchInput] = useState('');
    const [requests, setRequests] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageNum, setPageNum] = useState(1);
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 10;
    const [pageInputValue, setPageInputValue] = useState(null);
    const [pageInputError, setPageInputError] = useState('');
    
    // Edit page state
    const [products, setProducts] = useState([]);
    const [boxes, setBoxes] = useState([]);
    const [boxNumbers, setBoxNumbers] = useState([]);
    const [editingProducts, setEditingProducts] = useState({});
    const [editingBoxes, setEditingBoxes] = useState({});
    const [boxDimensions, setBoxDimensions] = useState({});
    const [selectedBoxes, setSelectedBoxes] = useState({});
    const [autofillChecked, setAutofillChecked] = useState({});

    const [productsPerBoxAuto, setProductsPerBoxAuto] = useState('');
    const [productsPerBoxAssign, setProductsPerBoxAssign] = useState('');
    const [assignWarning, setAssignWarning] = useState('');
    const [bulkCopyDims, setBulkCopyDims] = useState({ length: '', width: '', height: '', weight: '' });
    
    // View page state
    const [combinedLabelUrl, setCombinedLabelUrl] = useState('');
    const [totalLabelPrice, setTotalLabelPrice] = useState('0.00');
    const [finalizedBoxes, setFinalizedBoxes] = useState([]);
    const [labelImages, setLabelImages] = useState([]); // base64 PNG previews from Labelary

    const [selectedBoxCheckboxes, setSelectedBoxCheckboxes] = useState({});
    const [isShipped, setIsShipped] = useState(false);
    const [hasExistingLabels, setHasExistingLabels] = useState(false);

    const [bulkBeginDate, setBulkBeginDate] = useState('');
    const [bulkEndDate, setBulkEndDate] = useState('');
    const [bulkDateType, setBulkDateType] = useState('purchaseDate');
    const [bulkCombining, setBulkCombining] = useState(false);

    const [printServerPopup, setPrintServerPopup] = useState(false);

    const [pageSelectMode, setPageSelectMode] = useState('all'); // 'all' or 'range'
    const [pageRangeInput, setPageRangeInput] = useState('');
    const [pageRangeError, setPageRangeError] = useState('');

    const [dateRangeBegin, setDateRangeBegin] = useState('');
    const [dateRangeEnd, setDateRangeEnd] = useState('');
    const [userUID, setUserUID] = useState(null);
    const [filterLoaded, setFilterLoaded] = useState(false);
    const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
    const [sortField, setSortField] = useState('purchaseDate'); // 'purchaseDate', 'status', 'orderID', 'name', 'email'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'
    const [showSettingsPopup, setShowSettingsPopup] = useState(false);

    // Settings popup state
    const [settingsFilterMode, setSettingsFilterMode] = useState('lastPicked'); // 'lastPicked', 'selectedHere', 'dontSave'
    const [settingsSortMode, setSettingsSortMode] = useState('lastPicked');
    const [settingsPageMode, setSettingsPageMode] = useState('lastPicked');

    // Selected Here values
    const [settingsFilterValue, setSettingsFilterValue] = useState('all');
    const [settingsFilterSearchItem, setSettingsFilterSearchItem] = useState('all-fields');
    const [settingsFilterSearchInput, setSettingsFilterSearchInput] = useState(searchInput);
    const [settingsFilterDateBegin, setSettingsFilterDateBegin] = useState('');
    const [settingsFilterDateEnd, setSettingsFilterDateEnd] = useState('');
    const [settingsSortValue, setSettingsSortValue] = useState('purchaseDate');
    const [settingsSortDirection, setSettingsSortDirection] = useState('desc');
    const [settingsPageValue, setSettingsPageValue] = useState(1);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsDateError, setSettingsDateError] = useState('');
    const [dateRangeError, setDateRangeError] = useState('');
    const [settingsSnapshot, setSettingsSnapshot] = useState(null);

    const [editingAddress, setEditingAddress] = useState(false);
    const [addressFields, setAddressFields] = useState({
        shippingFirstName: '',
        shippingLastName: '',
        address1: '',
        address2: '',
        city: '',
        postalCode: '',
        state: ''
    });

    const [savingAddress, setSavingAddress] = useState(false);



    // Predefined box dimensions
    const presetDimensions = {
        '16X20X3-5': { length: '16', width: '20', height: '3', weight: '5' },
        '19X22X3-10': { length: '19', width: '22', height: '3', weight: '10' },
        '21X25X3-10': { length: '21', width: '25', height: '3', weight: '10' },
        '25X29X3-10': { length: '25', width: '29', height: '3', weight: '10' },
        '29X35X3-15': { length: '29', width: '35', height: '3', weight: '15' },
        '35X45X3-24': { length: '35', width: '45', height: '3', weight: '24' },
        '35X45X3-25': { length: '35', width: '45', height: '3', weight: '25' },
        '41X53X3-30': { length: '41', width: '53', height: '3', weight: '30' }
    };

    const imageSizeToBox = {
        '8x10': '16X20X3-5',
        '11x14': '16X20X3-5',
        '12x16': '19X22X3-10',
        '14x17': '19X22X3-10',
        '15x18': '21X25X3-10',
        '16x20': '21X25X3-10',
        '18x24': '25X29X3-10',
        '20x24': '25X29X3-10',
        '24x28': '29X35X3-15',
        '24x30': '29X35X3-15',
        '28x34': '35X45X3-24',
        '30x40': '35X45X3-25',
        '36x48': '41X53X3-30'
    };

    const parsePageRange = (rangeStr, totalPages) => {
        if (!rangeStr.trim()) return Array.from({ length: totalPages }, (_, i) => i);
        const indices = new Set();
        const parts = rangeStr.split(',');
        for (const part of parts) {
            const trimmed = part.trim();
            if (trimmed.includes('-')) {
                const [start, end] = trimmed.split('-').map(n => parseInt(n.trim()));
                if (!isNaN(start) && !isNaN(end)) {
                    for (let i = start; i <= end; i++) {
                        if (i >= 1 && i <= totalPages) indices.add(i - 1);
                    }
                }
            } else {
                const num = parseInt(trimmed);
                if (!isNaN(num) && num >= 1 && num <= totalPages) indices.add(num - 1);
            }
        }
        return [...indices].sort((a, b) => a - b);
    };

    const validatePageRange = (rangeStr) => {
        if (!rangeStr.trim()) return '';
        const parts = rangeStr.split(',');
        for (const part of parts) {
            const trimmed = part.trim();
            if (trimmed.includes('-')) {
                const [startStr, endStr] = trimmed.split('-');
                const start = parseInt(startStr.trim());
                const end = parseInt(endStr.trim());
                if (!isNaN(start) && !isNaN(end)) {
                    if (start >= end) return `Range "${trimmed}" is out of order — the start must be less than the end.`;
                }
            }
        }
        return '';
    };

    // ============ FETCH USER TYPE ============
    useEffect(() => {
        const fetchUserType = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user && user.uid) {
                    const response = await fetch(`${ENDPOINT_PATH}/api/get-account-type?user=${encodeURIComponent(user.uid)}`);
                    const accountType = await response.json();
                    setUserType(accountType.toLowerCase());
                }
            } catch (error) {
                console.error("Error fetching user type:", error);
            }
        };
        fetchUserType();
    }, []);


    useEffect(() => {
        const loadUserFilterPreference = async () => {
            try {
                // Get UID from localStorage
                const userStorage = localStorage.getItem('user');
                if (!userStorage) {
                    setFilterLoaded(true);
                    return;
                }
                
                const userData = JSON.parse(userStorage);
                const uid = userData.uid;
                setUserUID(uid);
                
                if (!uid) {
                    setFilterLoaded(true);
                    return;
                }
                
                // Fetch user's saved filter preference
                const response = await fetch(`${ENDPOINT_PATH}/api/userFilterRetrieval?uid=${uid}`);
                if (!response.ok) {
                    setFilterLoaded(true);
                    return;
                }
                
                const data = await response.json();
                const filterSave = data.filterSave;
                
                // Load settings modes
                const filterMode = data.filterMode || 'lastPicked';
                const sortMode = data.sortMode || 'lastPicked';
                const pageMode = data.pageMode || 'lastPicked';
                setSettingsFilterMode(filterMode);
                setSettingsSortMode(sortMode);
                setSettingsPageMode(pageMode);

                if (data.filterSave) {
                    const fv = data.filterSave.startsWith('DateRange') ? 'dateRange' :
                        data.filterSave.startsWith('Search') ? 'search' :
                        (data.filterSave || 'All').toLowerCase();
                    setSettingsFilterValue(fv);

                    if (data.filterSave.startsWith('DateRange||')) {
                        const parts = data.filterSave.split('||');
                        setSettingsFilterDateBegin(parts[1] || '');
                        setSettingsFilterDateEnd(parts[2] || '');
                    } else if (data.filterSave.startsWith('Search||')) {
                        const parts = data.filterSave.split('||');
                        setSettingsFilterSearchItem(parts[1] || 'all-fields');
                        setSettingsFilterSearchInput(parts[2] || '');
                    }
                }
                if (data.sortSave) {
                    const sp = data.sortSave.split('||');
                    setSettingsSortValue(sp[0] || 'purchaseDate');
                    setSettingsSortDirection(sp[1] || 'desc');
                }
                if (data.pageSave) {
                    setSettingsPageValue(Number(data.pageSave) || 1);
                }

                // Pre-populate settingsFilterSearchInput with current searchInput if filter mode is selectedHere
                if ((data.filterMode || 'lastPicked') === 'selectedHere' && data.filterSave?.startsWith('Search||')) {
                    const parts = data.filterSave.split('||');
                    setSettingsFilterSearchInput(parts[2] || searchInput);
                }

                // Load sort preference
                if (sortMode === 'lastPicked') {
                    try {
                        const sortResponse = await fetch(`${ENDPOINT_PATH}/api/userSortRetrieval?uid=${uid}`);
                        if (sortResponse.ok) {
                            const sortData = await sortResponse.json();
                            if (sortData.sortSave) {
                                const parts = sortData.sortSave.split('||');
                                setSortField(parts[0] || 'purchaseDate');
                                setSortDirection(parts[1] || 'desc');
                                setSettingsSortValue(parts[0] || 'purchaseDate');
                                setSettingsSortDirection(parts[1] || 'desc');
                            }
                        }
                    } catch (e) {}
                } else if (sortMode === 'selectedHere' && data.sortSave) {
                    const parts = data.sortSave.split('||');
                    setSortField(parts[0] || 'purchaseDate');
                    setSortDirection(parts[1] || 'desc');
                }
                // dontSave -> default purchaseDate desc (already set)

                // Load page preference
                if (pageMode === 'lastPicked') {
                    try {
                        const pageResponse = await fetch(`${ENDPOINT_PATH}/api/userPageRetrieval?uid=${uid}`);
                        if (pageResponse.ok) {
                            const pageData = await pageResponse.json();
                            if (pageData.pageSave) setPageNum(Number(pageData.pageSave) || 1);
                        }
                    } catch (e) {}
                } else if (pageMode === 'selectedHere' && data.pageSave) {
                    setPageNum(Number(data.pageSave) || 1);
                }

                // Parse the filter preference — skip if dontSave, always default to 'all'
                if (filterMode !== 'dontSave') {
                    if (filterSave === 'All' || filterSave === 'Unfinished' || filterSave === 'Finished') {
                        setFilterType(filterSave.toLowerCase());
                    } else if (filterSave.startsWith('DateRange||')) {
                        // Format: DateRange||YYYY-MM-DD||YYYY-MM-DD
                        const parts = filterSave.split('||');
                        if (parts.length === 3) {
                            setFilterType('dateRange');
                            setDateRangeBegin(parts[1]);
                            setDateRangeEnd(parts[2]);
                        }
                    } else if (filterSave.startsWith('Search||')) {
                        // Format: Search||searchItem||searchInput
                        const parts = filterSave.split('||');
                        if (parts.length === 3) {
                            setFilterType('search');
                            setSearchItem(parts[1]);
                            setSearchInput(parts[2]);
                        }
                    }
                }
                // dontSave: filterType stays at default 'all' (already initialized that way)
                
            } catch (error) {
                console.error('Error loading filter preference:', error);
            } finally {
                setFilterLoaded(true);
            }
        };
        
        loadUserFilterPreference();
    }, []);
    
    // ============ PAGE 1: LIST FUNCTIONS ============
    
    const fetchCount = useCallback(async (overrideFilter, overrideDates, overrideSearch) => {
        if (!filterLoaded) return;
        
        const ft = overrideFilter?.filterType ?? filterType;
        const drb = overrideDates?.dateRangeBegin ?? dateRangeBegin;
        const dre = overrideDates?.dateRangeEnd ?? dateRangeEnd;
        const si = overrideSearch?.searchItem ?? searchItem;
        const sinp = overrideSearch?.searchInput ?? searchInput;

        try {
            let url = `${ENDPOINT_PATH}/api/count-dimension-requests?filterType=${ft}`;
            
            if (ft === 'dateRange') {
                if (drb && dre) {
                    url += `&beginDate=${drb}&endDate=${dre}`;
                } else {
                    setTotalCount(0);
                    return 0;
                }
            } else if (ft === 'search' && sinp.trim()) {
                url += `&searchItem=${si}&searchInput=${encodeURIComponent(sinp)}`;
            }
            
            const response = await fetch(url);
            const data = await response.json();
            const count = data.count || 0;
            setTotalCount(count);
            return count;
        } catch (error) {
            console.error('Error fetching count:', error);
            setTotalCount(0);
            return 0;
        }
    }, [filterType, searchItem, searchInput, dateRangeBegin, dateRangeEnd, filterLoaded]);

    const fetchRequests = useCallback(async (overrideSortField, overrideSortDir) => {
        if (!filterLoaded) return;
        
        const sf = overrideSortField ?? sortField;
        const sd = overrideSortDir ?? sortDirection;

        setLoading(true);
        try {
            const beginRange = (pageNum - 1) * itemsPerPage + 1;
            const endRange = pageNum * itemsPerPage;
            
            let url = `${ENDPOINT_PATH}/api/get-dimension-requests?filterType=${filterType}&beginRange=${beginRange}&endRange=${endRange}&sortField=${sf}&sortDirection=${sd}`;
            
            if (filterType === 'dateRange') {
                if (dateRangeBegin && dateRangeEnd) {
                    url += `&beginDate=${dateRangeBegin}&endDate=${dateRangeEnd}`;
                } else {
                    setRequests([]);
                    setLoading(false);
                    return;
                }
            } else if (filterType === 'search' && searchInput.trim()) {
                url += `&searchItem=${searchItem}&searchInput=${encodeURIComponent(searchInput)}`;
            }
            
            const response = await fetch(url);
            const data = await response.json();
            setRequests(data.requests || []);
        } catch (error) {
            console.error('Error fetching requests:', error);
            setRequests([]);
        }
        setLoading(false);
    }, [filterType, searchItem, searchInput, pageNum, dateRangeBegin, dateRangeEnd, filterLoaded, sortField, sortDirection]);

    const handleFilterChange = (newFilter) => {
        setFilterType(newFilter);
        setPageNum(1);
        if (settingsFilterMode === 'lastPicked') {
            saveFilterPreference(newFilter);
        }
        if (settingsPageMode === 'lastPicked') {
            savePagePreference(1);
        }
    };

    const handleDateRangeChange = (begin, end) => {
        setDateRangeBegin(begin);
        setDateRangeEnd(end);
        setPageNum(1);
        if (settingsPageMode === 'lastPicked') {
            savePagePreference(1);
        }

        if (begin && end) {
            if (new Date(begin) > new Date(end)) {
                setDateRangeError('Begin date must be before or equal to end date');
            } else {
                setDateRangeError('');
                if (settingsFilterMode === 'lastPicked') {
                    saveFilterPreference('dateRange', { beginDate: begin, endDate: end });
                }
            }
        } else {
            setDateRangeError('');
        }
    };

    const handleSearchInputChange = (value) => {
        setSearchInput(value);
        setPageNum(1);
        if (settingsPageMode === 'lastPicked') {
            savePagePreference(1);
        }
        
        if (settingsFilterMode !== 'lastPicked') return;
        
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer);
        }
        
        const timer = setTimeout(() => {
            saveFilterPreference('search', { searchInput: value });
        }, 2000);
        
        setSearchDebounceTimer(timer);
    };

    const handleSearchItemChange = (item) => {
        setSearchItem(item);
        setPageNum(1);
        if (settingsPageMode === 'lastPicked') {
            savePagePreference(1);
        }
        if (settingsFilterMode === 'lastPicked') {
            saveFilterPreference('search', { searchItem: item });
        }
    };

    const handleSortClick = (field) => {
        let newDir = 'asc';
        if (sortField === field) {
            newDir = sortDirection === 'asc' ? 'desc' : 'asc';
        }
        setSortField(field);
        setSortDirection(newDir);
        saveSortPreference(field, newDir);
    };

    const handleClear = async () => {
        // Determine defaults based on settings modes
        // For filter
        let newFilter = 'all';
        let newDateBegin = '';
        let newDateEnd = '';
        let newSearchItem = 'all-fields';
        let newSearchInput = '';
        if (settingsFilterMode === 'selectedHere') {
            newFilter = settingsFilterValue;
            newDateBegin = settingsFilterDateBegin;
            newDateEnd = settingsFilterDateEnd;
            newSearchItem = settingsFilterSearchItem;
            newSearchInput = settingsFilterSearchInput;
        }
        // lastPicked -> default 'all', dontSave -> default 'all' (already set)
        if (settingsFilterMode === 'lastPicked') {
            saveFilterPreference('all');
        }

        // For sort
        let newSort = 'purchaseDate';
        let newSortDir = 'desc';
        if (settingsSortMode === 'selectedHere') {
            newSort = settingsSortValue;
            newSortDir = settingsSortDirection;
        }
        // lastPicked -> default 'purchaseDate'/'desc', dontSave -> default 'purchaseDate'/'desc' (already set)
        if (settingsSortMode === 'lastPicked') {
            try {
                await fetch(`${ENDPOINT_PATH}/api/saveUserSort`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uid: userUID, sortSave: 'purchaseDate||desc' })
                });
            } catch (e) {}
        }

        // For page
        let newPage = 1;
        if (settingsPageMode === 'selectedHere') {
            newPage = settingsPageValue || 1;
        }
        // lastPicked and dontSave -> page 1
        if (settingsPageMode === 'lastPicked') {
            savePagePreference(1);
        }

        setFilterType(newFilter);
        setDateRangeBegin(newDateBegin);
        setDateRangeEnd(newDateEnd);
        setSearchItem(newSearchItem);
        setSearchInput(newSearchInput);
        setSortField(newSort);
        setSortDirection(newSortDir);
        setPageNum(newPage);
    };

    const saveSortPreference = async (field, dir) => {
        if (!userUID) return;
        // Only save if sort mode is lastPicked
        if (settingsSortMode !== 'lastPicked') return;
        try {
            await fetch(`${ENDPOINT_PATH}/api/saveUserSort`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid: userUID, sortSave: `${field}||${dir}` })
            });
        } catch (e) {}
    };

    const savePagePreference = async (page) => {
        if (!userUID) return;
        if (settingsPageMode !== 'lastPicked') return;
        try {
            await fetch(`${ENDPOINT_PATH}/api/saveUserPage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid: userUID, pageSave: page })
            });
        } catch (e) {}
    };

    // Fetch page options for settings popup based on current settings selections
    const fetchSettingsPageOptions = async () => {
        // No longer needed - page lock uses manual number input
    };

    const handleSettingsSave = async () => {
        if (!userUID) return;

        setSettingsDateError('');

        // Validate selectedHere fields
        if (settingsFilterMode === 'selectedHere') {
            if (settingsFilterValue === 'dateRange') {
                if (!settingsFilterDateBegin || !settingsFilterDateEnd) return;
                if (new Date(settingsFilterDateBegin) > new Date(settingsFilterDateEnd)) {
                    setSettingsDateError('Begin date must be before or equal to end date');
                    return;
                }
            }
            if (settingsFilterValue === 'search' && !settingsFilterSearchInput.trim()) return;
        }
        if (settingsSortMode === 'selectedHere' && !settingsSortValue) return;
        if (settingsPageMode === 'selectedHere' && !settingsPageValue) return;

        setSettingsSaving(true);
        try {
            // Build payloads
            const payload = {
                uid: userUID,
                filterMode: settingsFilterMode,
                sortMode: settingsSortMode,
                pageMode: settingsPageMode,
            };

            if (settingsFilterMode === 'selectedHere') {
                let filterSave = '';
                if (settingsFilterValue === 'all' || settingsFilterValue === 'unfinished' || settingsFilterValue === 'finished') {
                    filterSave = settingsFilterValue.charAt(0).toUpperCase() + settingsFilterValue.slice(1);
                } else if (settingsFilterValue === 'dateRange') {
                    filterSave = `DateRange||${settingsFilterDateBegin}||${settingsFilterDateEnd}`;
                } else if (settingsFilterValue === 'search') {
                    filterSave = `Search||${settingsFilterSearchItem}||${settingsFilterSearchInput}`;
                }
                payload.filterSave = filterSave;
            }

            if (settingsSortMode === 'selectedHere') {
                payload.sortSave = `${settingsSortValue}||${settingsSortDirection}`;
            }

            if (settingsPageMode === 'selectedHere') {
                payload.pageSave = settingsPageValue;
            }

            await fetch(`${ENDPOINT_PATH}/api/saveUserSettings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            setShowSettingsPopup(false);
        } catch (e) {
            console.error('Error saving settings:', e);
        }
        setSettingsSaving(false);
    };

    const handleSettingsResetToDefaults = () => {
        setSettingsFilterMode('dontSave');
        setSettingsSortMode('dontSave');
        setSettingsPageMode('dontSave');
        setSettingsFilterValue('all');
        setSettingsFilterSearchItem('all-fields');
        setSettingsFilterSearchInput('');
        setSettingsFilterDateBegin('');
        setSettingsFilterDateEnd('');
        setSettingsSortValue('purchaseDate');
        setSettingsSortDirection('desc');
        setSettingsPageValue(1);
    };

    const isSettingsSaveDisabled = () => {
        if (settingsFilterMode === 'selectedHere') {
            if (settingsFilterValue === 'dateRange' && (!settingsFilterDateBegin || !settingsFilterDateEnd)) return true;
            if (settingsFilterValue === 'search' && !settingsFilterSearchInput.trim()) return true;
        }
        if (settingsSortMode === 'selectedHere' && !settingsSortValue) return true;
        if (settingsPageMode === 'selectedHere' && (!settingsPageValue || settingsPageValue < 1)) return true;
        return false;
    };

    

    useEffect(() => {
        if (currentPage === 'list') {
            fetchCount().then(count => {
                const totalPgs = Math.ceil(count / itemsPerPage);
                if (count === 0 || pageNum > totalPgs) {
                    if (settingsPageMode === 'lastPicked') {
                        savePagePreference(1);
                    }
                    // For 'selectedHere' and 'dontSave': only reset live table, never touch saved setting
                    setPageNum(1);
                }
            });
            fetchRequests();
        }
    }, [currentPage, filterType, sortField, sortDirection, fetchCount, fetchRequests]);

    const handleInteract = async (request) => {
        // Check if button is disabled (shipped order)
        if (request.isDisabled) {
            return; // Do nothing if order is shipped
        }
        
        setCurrentOrderID(request.orderID);
        setOrderInfo({
            orderID: request.orderID,
            name: request.name,
            email: request.email
        });
        
        // Set shipped status
        setIsShipped(request.shipped?.toLowerCase() === 'yes');
        
        if (request.status === 'Begin') {
            try {
                await fetch(`${ENDPOINT_PATH}/api/start-edit-status-update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderID: request.orderID })
                });
            } catch (error) {
                console.error('Error updating status:', error);
            }
        }
        
        if (request.buttonLink === '/edit') {
            setCurrentPage('edit');
        } else if (request.buttonLink === '/view') {
            setCurrentPage('view');
        }
    };

    const loadAddressData = async () => {
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/get-dimension-requests?filterType=all&beginRange=1&endRange=1000`);
            const data = await response.json();
            
            const currentOrder = data.requests.find(r => r.orderID === currentOrderID);
            if (currentOrder) {
                // Fetch the full order data
                const orderDoc = await fetch(`${ENDPOINT_PATH}/api/load-products?orderID=${currentOrderID}`);
                const orderData = await orderDoc.json();
                
                // Get the full document to access address fields
                const fullOrderResponse = await fetch(`${ENDPOINT_PATH}/api/get-boxes?orderID=${currentOrderID}`);
                const fullOrderData = await fullOrderResponse.json();
                
                // Actually we need to add a new endpoint or modify existing to return address data
                // For now, let's fetch directly from the document
                const docResponse = await fetch(`${ENDPOINT_PATH}/api/get-address-data?orderID=${currentOrderID}`);
                const addressData = await docResponse.json();
                
                setAddressFields({
                    shippingFirstName: addressData.shippingFirstName || '',
                    shippingLastName: addressData.shippingLastName || '',
                    address1: addressData.address1 || '',
                    address2: addressData.address2 || '',
                    city: addressData.city || '',
                    postalCode: addressData.postalCode || '',
                    state: addressData.state || ''
                });
            }
        } catch (error) {
            console.error('Error loading address data:', error);
        }
    };

    const handleAddressFieldChange = (field, value) => {
        setAddressFields(prev => ({
            ...prev,
            [field]: value
        }));
    };


    const handleSaveAddress = async () => {
        // Validate all required fields are filled
        const requiredFields = ['shippingFirstName', 'shippingLastName', 'address1', 'city', 'postalCode', 'state'];
        const emptyFields = [];
        
        requiredFields.forEach(field => {
            if (!addressFields[field] || addressFields[field].trim() === '') {
                emptyFields.push(field);
            }
        });
        
        if (emptyFields.length > 0) {
            const fieldNames = {
                shippingFirstName: 'First Name',
                shippingLastName: 'Last Name',
                address1: 'Address Line 1',
                city: 'City',
                postalCode: 'Postal Code',
                state: 'State'
            };
            
            const missingFieldNames = emptyFields.map(f => fieldNames[f]).join(', ');
            alert(`Please fill in the following required fields: ${missingFieldNames}`);
            return;
        }

        setSavingAddress(true);
        
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/update-address`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    ...addressFields
                })
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                // Stay in edit mode on error
                setSavingAddress(false);
                
                // Handle validation errors
                if (data.missingFields) {
                    const fieldNames = {
                        shippingFirstName: 'First Name',
                        shippingLastName: 'Last Name',
                        address1: 'Address Line 1',
                        city: 'City',
                        postalCode: 'Postal Code',
                        state: 'State'
                    };
                    
                    const missing = Object.entries(data.missingFields)
                        .filter(([_, isMissing]) => isMissing)
                        .map(([field, _]) => fieldNames[field]);
                    
                    alert(`The following fields are missing: ${missing.join(', ')}`);
                } else {
                    alert(`Address validation failed: ${data.details || data.error}`);
                }
                return;
            }
            
            // Success - update address fields with validated address from FedEx
            if (data.validatedAddress) {
                setAddressFields({
                    shippingFirstName: data.validatedAddress.shippingFirstName,
                    shippingLastName: data.validatedAddress.shippingLastName,
                    address1: data.validatedAddress.address1,
                    address2: data.validatedAddress.address2,
                    city: data.validatedAddress.city,
                    state: data.validatedAddress.state,
                    postalCode: data.validatedAddress.postalCode
                });
                
                // Update orderInfo with new address
                setOrderInfo(prev => ({
                    ...prev,
                    shippingAddress: data.validatedAddress.fullAddress
                }));
            }
            
            // Exit edit mode
            setEditingAddress(false);
            setSavingAddress(false);
            
            alert('Address validated and updated successfully!');
            
        } catch (error) {
            console.error('Error saving address:', error);
            setSavingAddress(false);
            alert('Failed to update address. Please try again.');
        }
    };

    // ---- Shared helper: fetch combined ZPL from API ----
    const fetchBulkZpl = async () => {
        if (!bulkBeginDate || !bulkEndDate) {
            alert('Please select both begin and end dates');
            return null;
        }
        const begin = new Date(bulkBeginDate);
        const end   = new Date(bulkEndDate);
        if (begin > end) {
            alert('Begin date must be before or equal to end date');
            return null;
        }
        const response = await fetch(`${ENDPOINT_PATH}/api/bulk-combine-labels`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                beginDate: bulkBeginDate,
                endDate:   bulkEndDate,
                dateType:  bulkDateType
            })
        });
        if (!response.ok) {
            const error = await response.json();
            alert(error.error || 'Failed to combine labels');
            return null;
        }
        return await response.text();
    };

    // ---- Shared helper: render ZPL blocks → base64 PNGs (same constants as page 3 Thermal Print) ----
    const renderBulkZplToBase64s = async (zplText) => {
        const labelMatches = (zplText.match(/\^XA[\s\S]*?\^XZ/g) || []).reverse();
        if (labelMatches.length === 0) {
            alert('No labels found in the combined ZPL.');
            return null;
        }

        const outW       = Math.round(2.3125 * 300); // 694 px
        const outH       = Math.round(4      * 300); // 1200 px
        const marginLeft  = 0;
        const marginRight = 20;
        const marginTop   = 0;
        const marginBot   = 0;
        const offsetX     = 10;
        const offsetY     = 10;

        const renderLabel = (blob) => new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(blob);
            img.onload = () => {
                const offscreen = document.createElement('canvas');
                offscreen.width  = img.width;
                offscreen.height = img.height;
                const offCtx = offscreen.getContext('2d');
                offCtx.drawImage(img, 0, 0);
                const pixels = offCtx.getImageData(0, 0, img.width, img.height).data;

                const isWhite = (x, y) => {
                    const i = (y * img.width + x) * 4;
                    return pixels[i] > 230 && pixels[i+1] > 230 && pixels[i+2] > 230;
                };

                let top = 0;
                topLoop: for (let y = 0; y < img.height; y++) {
                    for (let x = 0; x < img.width; x++) {
                        if (!isWhite(x, y)) { top = y; break topLoop; }
                    }
                }
                let bottom = img.height - 1;
                bottomLoop: for (let y = img.height - 1; y >= 0; y--) {
                    for (let x = 0; x < img.width; x++) {
                        if (!isWhite(x, y)) { bottom = y; break bottomLoop; }
                    }
                }
                let left = 0;
                leftLoop: for (let x = 0; x < img.width; x++) {
                    for (let y = 0; y < img.height; y++) {
                        if (!isWhite(x, y)) { left = x; break leftLoop; }
                    }
                }
                let right = img.width - 1;
                rightLoop: for (let x = img.width - 1; x >= 0; x--) {
                    for (let y = 0; y < img.height; y++) {
                        if (!isWhite(x, y)) { right = x; break rightLoop; }
                    }
                }

                const pad  = 10;
                const srcX = Math.max(0, left - pad);
                const srcY = Math.max(0, top  - pad);
                const srcW = Math.min(img.width,  right  + pad) - srcX;
                const srcH = Math.min(img.height, bottom + pad) - srcY;

                const canvas = document.createElement('canvas');
                canvas.width  = outW;
                canvas.height = outH;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, outW, outH);

                const contentW = outW - marginLeft - marginRight;
                const contentH = outH - marginTop  - marginBot;
                const destX    = (-outW / 2) + marginLeft + offsetX;
                const destY    = (-outH / 2) + marginTop  + offsetY;

                ctx.translate(outW / 2, outH / 2);
                ctx.rotate(Math.PI);
                ctx.drawImage(img, srcX, srcY, srcW, srcH, destX, destY, contentW, contentH);

                URL.revokeObjectURL(objectUrl);
                resolve(canvas.toDataURL('image/png').split(',')[1]);
            };
            img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
            img.src = objectUrl;
        });

        const bulkRes = await fetch(`${ENDPOINT_PATH}/api/proxy-labelary-bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zpls: labelMatches })
        });
        if (!bulkRes.ok) throw new Error('Label render failed');
        const bulkData = await bulkRes.json();
        const base64s = await Promise.all(
            (bulkData.images || []).map(async (base64) => {
                const byteChars = atob(base64);
                const byteArr = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                const blob = new Blob([byteArr], { type: 'image/png' });
                return await renderLabel(blob);
            })
        );
        return base64s;
    };

    // ---- Shared helper for LASER: render ZPL blocks → base64 data URLs (NO rotation, same as page 3 laser) ----
    const renderBulkZplForLaser = async (zplText) => {
        const labelMatches = zplText.match(/\^XA[\s\S]*?\^XZ/g) || [];
        if (labelMatches.length === 0) {
            alert('No labels found in the combined ZPL.');
            return null;
        }

        const bulkRes = await fetch(`${ENDPOINT_PATH}/api/proxy-labelary-bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zpls: labelMatches })
        });
        if (!bulkRes.ok) throw new Error('Label render failed');
        const bulkData = await bulkRes.json();
        const dataUrls = await Promise.all(
            (bulkData.images || []).map(async (base64) => {
                const byteChars = atob(base64);
                const byteArr = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                const blob = new Blob([byteArr], { type: 'image/png' });
                return await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            })
        );
        return dataUrls;
    };

    // ---- Download Thermal Labels (.pyw) — exact same format as page 3 ----
    const handleBulkDownloadThermalLabels = async () => {
        setBulkCombining(true);
        try {
            const zplText = await fetchBulkZpl();
            if (!zplText) { setBulkCombining(false); return; }

            const renderedBase64s = await renderBulkZplToBase64s(zplText);
            if (!renderedBase64s) { setBulkCombining(false); return; }

            const imagesArrayLiteral = '[' + renderedBase64s.map(b => `"${b}"`).join(',\n') + ']';

            const pywContent = `#!/usr/bin/env python3
import sys
import subprocess
import importlib
import platform

IS_WINDOWS = platform.system() == "Windows"
IS_MAC     = platform.system() == "Darwin"

if IS_WINDOWS:
    for pkg, imp in [("pywin32", "win32com"), ("Pillow", "PIL")]:
        try:
            importlib.import_module(imp)
        except ImportError:
            subprocess.check_call(
                [sys.executable, "-m", "pip", "install", "--user", pkg],
                stdout=subprocess.DEVNULL,
                stderr=subprocess.DEVNULL,
            )
else:
    try:
        importlib.import_module("PIL")
    except ImportError:
        subprocess.check_call(
            [sys.executable, "-m", "pip", "install", "--user", "Pillow"],
            stdout=subprocess.DEVNULL,
            stderr=subprocess.DEVNULL,
        )

import threading, time, base64, io, tempfile, os
import tkinter as tk
from tkinter import scrolledtext, ttk
from PIL import Image

if IS_WINDOWS:
    import win32print
    import win32ui
    import win32con
    from PIL import ImageWin

LABEL_IMAGES_B64 = ${imagesArrayLiteral}

LABEL_WIDTH_IN  = 2.3125
LABEL_HEIGHT_IN = 4.0


def list_all_printers():
    if IS_WINDOWS:
        return [p[2] for p in win32print.EnumPrinters(
            win32print.PRINTER_ENUM_LOCAL | win32print.PRINTER_ENUM_CONNECTIONS)]
    else:
        try:
            result = subprocess.run(["lpstat", "-p"], capture_output=True, text=True)
            printers = []
            for line in result.stdout.splitlines():
                if line.startswith("printer "):
                    parts = line.split()
                    if len(parts) >= 2:
                        printers.append(parts[1])
            return printers
        except Exception:
            return []


def find_dymo_default(printers):
    for p in printers:
        if "dymo" in p.lower():
            return p
    return printers[0] if printers else ""


def print_via_gdi(printer_name, img_b64, log_fn):
    img_bytes = base64.b64decode(img_b64)
    img = Image.open(io.BytesIO(img_bytes)).convert("RGB")
    log_fn("  Printing via GDI to: " + printer_name, "dim")
    hprinter = win32print.OpenPrinter(printer_name)
    try:
        devmode = win32print.GetPrinter(hprinter, 8)["pDevMode"]
        devmode.PaperSize   = 0
        devmode.Fields      |= win32con.DM_PAPERSIZE | win32con.DM_PAPERLENGTH | win32con.DM_PAPERWIDTH
        devmode.PaperWidth  = int(2.3125 * 254)
        devmode.PaperLength = int(4.0    * 254)
        devmode.Orientation = win32con.DMORIENT_PORTRAIT
    finally:
        win32print.ClosePrinter(hprinter)
    hdc = win32ui.CreateDC()
    hdc.CreatePrinterDC(printer_name)
    pw = hdc.GetDeviceCaps(win32con.HORZRES)
    ph = hdc.GetDeviceCaps(win32con.VERTRES)
    log_fn("  Print canvas: " + str(pw) + "x" + str(ph) + " px", "dim")
    img_fit = img.resize((pw, ph), Image.LANCZOS)
    dib = ImageWin.Dib(img_fit)
    try:
        hdc.StartDoc("MCF Label")
        hdc.StartPage()
        dib.draw(hdc.GetHandleOutput(), (0, 0, pw, ph))
        hdc.EndPage()
        hdc.EndDoc()
        log_fn("  GDI print complete.", "ok")
    finally:
        hdc.DeleteDC()


def print_via_lp(printer_name, img_b64_list, log_fn):
    tmp_paths = []
    for img_b64 in img_b64_list:
        img_bytes = base64.b64decode(img_b64)
        img = Image.open(io.BytesIO(img_bytes)).convert("RGB")
        MAC_CROP_TOP    = 0   # px cropped from top of source image
        MAC_CROP_BOTTOM = 0   # px cropped from bottom of source image
        MAC_CROP_LEFT   = 0   # px cropped from left of source image
        MAC_CROP_RIGHT  = 0   # px cropped from right of source image
        MAC_OFFSET_X    = 4  # px to shift content horizontally (positive = right)
        MAC_OFFSET_Y    = 38  # px to shift content vertically (negative = up, positive = down)
        MAC_SCALE_X     = 1.00  # horizontal stretch (increase to push content closer to sides)
        MAC_SCALE_Y     = 0.98  # vertical scale (keep your working value)
        w, h = img.size
        img = img.crop((
            MAC_CROP_LEFT,
            MAC_CROP_TOP,
            w - MAC_CROP_RIGHT if MAC_CROP_RIGHT > 0 else w,
            h - MAC_CROP_BOTTOM if MAC_CROP_BOTTOM > 0 else h
        ))
        cropped_w, cropped_h = img.size
        scaled_w = int(cropped_w * MAC_SCALE_X)
        scaled_h = int(cropped_h * MAC_SCALE_Y)
        img = img.resize((scaled_w, scaled_h), Image.LANCZOS)
        new_img = Image.new("RGB", (w, h), (255, 255, 255))
        paste_x = MAC_CROP_LEFT + MAC_OFFSET_X
        paste_y = MAC_CROP_TOP  + MAC_OFFSET_Y
        new_img.paste(img, (paste_x, paste_y))
        img = new_img
        with tempfile.NamedTemporaryFile(suffix=".png", delete=False) as tmp:
            tmp_path = tmp.name
            img.save(tmp_path, format="PNG", dpi=(300, 300))
        tmp_paths.append(tmp_path)
    log_fn("  Printing via lp to: " + printer_name, "dim")
    try:
        result = subprocess.run(
            [
                "lp",
                "-d", printer_name,
                "-o", "media=Custom.2.3125x4in",
                "-o", "fit-to-page",
                "-o", "orientation-requested=3",
                "-o", "page-top=0",
            ] + tmp_paths,
            capture_output=True, text=True
        )
        if result.returncode != 0:
            raise RuntimeError(result.stderr.strip() or "lp failed")
        log_fn("  lp print submitted.", "ok")
    finally:
        for p in tmp_paths:
            os.unlink(p)


class App(tk.Tk):
    def __init__(self):
        super().__init__()
        self.title("Label Printer – ${bulkBeginDate} to ${bulkEndDate}")
        self.geometry("640x560")
        self.resizable(True, True)

        if IS_MAC:
            style = ttk.Style(self)
            style.theme_use("aqua")

        if IS_WINDOWS:
            self.configure(bg="#f5f5f5")
            BODY_BG  = "#f5f5f5"
            LABEL_FG = "#333333"
            HINT_FG  = "#666666"
        else:
            BODY_BG  = None
            LABEL_FG = None
            HINT_FG  = None

        # Header
        hdr = tk.Frame(self, bg="#ff4172", pady=10)
        hdr.pack(fill="x")
        tk.Label(hdr, text="MyCustomFrame  |  Label Printer",
                 fg="white", bg="#ff4172",
                 font=("Helvetica", 14, "bold")).pack()
        tk.Label(hdr, text="${bulkBeginDate} to ${bulkEndDate}  –  ${renderedBase64s.length} label(s) ready to print",
                 fg="#ffcdd8", bg="#ff4172",
                 font=("Helvetica", 10)).pack()

        # Printer selector
        sel_kwargs = {"padx": 12, "pady": 8}
        if BODY_BG: sel_kwargs["bg"] = BODY_BG
        sel_frame = tk.Frame(self, **sel_kwargs)
        sel_frame.pack(fill="x")

        lbl_kw = {"font": ("Helvetica", 10, "bold")}
        if BODY_BG:  lbl_kw["bg"] = BODY_BG
        if LABEL_FG: lbl_kw["fg"] = LABEL_FG
        tk.Label(sel_frame, text="Printer:", **lbl_kw).pack(side="left")

        self.printer_var = tk.StringVar()
        self.printer_combo = ttk.Combobox(
            sel_frame, textvariable=self.printer_var,
            state="readonly", font=("Helvetica", 10), width=38)
        self.printer_combo.pack(side="left", padx=(8, 8))

        ttk.Button(sel_frame, text="↻ Refresh",
                   command=self.refresh_printers).pack(side="left")

        self.refresh_printers()

        # Log area
        lf_kw = {"padx": 12, "pady": 4}
        if BODY_BG: lf_kw["bg"] = BODY_BG
        lf = tk.Frame(self, **lf_kw)
        lf.pack(fill="both", expand=True)

        log_lbl_kw = {"font": ("Helvetica", 10, "bold"), "anchor": "w"}
        if BODY_BG:  log_lbl_kw["bg"] = BODY_BG
        if LABEL_FG: log_lbl_kw["fg"] = LABEL_FG
        tk.Label(lf, text="Log:", **log_lbl_kw).pack(fill="x")

        self.txt = scrolledtext.ScrolledText(
            lf, state="disabled", font=("Courier New", 9),
            bg="#1e1e1e", fg="#d4d4d4", relief="flat", bd=0, height=20)
        self.txt.pack(fill="both", expand=True, pady=(4, 0))
        for tag, color in [("ok","#4ec9b0"),("err","#f44747"),("step","#c586c0"),
                            ("info","#569cd6"),("warn","#dcdcaa"),("dim","#9cdcfe")]:
            self.txt.tag_config(tag, foreground=color)

        # Bottom bar
        bot_kw = {"padx": 12, "pady": 8}
        if BODY_BG: bot_kw["bg"] = BODY_BG
        bot = tk.Frame(self, **bot_kw)
        bot.pack(fill="x")

        status_lbl_kw = {"font": ("Helvetica", 9), "anchor": "w"}
        if BODY_BG: status_lbl_kw["bg"] = BODY_BG
        if HINT_FG: status_lbl_kw["fg"] = HINT_FG
        self.status_var = tk.StringVar(value="Ready.")
        tk.Label(bot, textvariable=self.status_var, **status_lbl_kw).pack(
            side="left", expand=True, fill="x")

        if IS_WINDOWS:
            style = ttk.Style(self)
            style.theme_use("clam")
            style.configure("Pink.TButton",
                             background="#ff4172", foreground="white",
                             font=("Helvetica", 10, "bold"), relief="flat", padding=6)
            style.map("Pink.TButton",
                      background=[("active", "#e63361"), ("pressed", "#c82b54")])
            style.configure("Grey.TButton",
                             background="#6c757d", foreground="white",
                             font=("Helvetica", 10), relief="flat", padding=6)
            style.map("Grey.TButton",
                      background=[("active", "#5a6268"), ("pressed", "#495057")])
            self.print_btn = ttk.Button(bot, text="Print Labels",
                                        style="Pink.TButton", command=self.start_print)
            self.print_btn.pack(side="right", padx=(8, 0))
            ttk.Button(bot, text="Close",
                       style="Grey.TButton", command=self.destroy).pack(side="right")
        else:
            self.print_btn = ttk.Button(bot, text="Print Labels", command=self.start_print)
            self.print_btn.pack(side="right", padx=(8, 0))
            ttk.Button(bot, text="Close", command=self.destroy).pack(side="right")

        self._worker = None

    def refresh_printers(self):
        printers = list_all_printers()
        if not printers:
            self.printer_combo["values"] = ["(no printers found)"]
            self.printer_var.set("(no printers found)")
            return
        self.printer_combo["values"] = printers
        current = self.printer_var.get()
        if current in printers:
            pass
        else:
            default = find_dymo_default(printers)
            self.printer_var.set(default)

    def log(self, msg, tag="dim"):
        def _do():
            self.txt.configure(state="normal")
            self.txt.insert("end", "[" + time.strftime("%H:%M:%S") + "]  ", "dim")
            self.txt.insert("end", msg + "\\n", tag)
            self.txt.configure(state="disabled")
            self.txt.see("end")
        self.after_idle(_do)

    def set_status(self, msg):
        self.after_idle(lambda: self.status_var.set(msg))

    def start_print(self):
        if self._worker and self._worker.is_alive():
            return
        printer = self.printer_var.get()
        if not printer or printer == "(no printers found)":
            self.log("No printer selected.", "err")
            return
        self.print_btn.configure(state="disabled")
        self._worker = threading.Thread(target=self._run, args=(printer,), daemon=True)
        self._worker.start()

    def _run(self, printer):
        try:
            run_job(self, printer)
        except Exception as exc:
            self.log("FATAL: " + str(exc), "err")
            self.set_status("Failed – see log.")
        finally:
            self.after_idle(lambda: self.print_btn.configure(state="normal"))


def run_job(app, printer_name):
    app.log(str(len(LABEL_IMAGES_B64)) + " pre-rendered label(s) loaded.", "info")
    app.log("Label size: 2.3125\\" x 4.0\\" @ 300 DPI", "info")
    app.log("Platform: " + platform.system(), "info")
    app.log("Printer: " + printer_name, "info")

    if IS_WINDOWS:
        for i, b64 in enumerate(LABEL_IMAGES_B64, 1):
            app.log("Printing label " + str(i) + " of " + str(len(LABEL_IMAGES_B64)) + " ...", "step")
            app.set_status("Printing " + str(i) + "/" + str(len(LABEL_IMAGES_B64)) + " ...")
            print_via_gdi(printer_name, b64, app.log)
            app.log("Label " + str(i) + " sent to printer.", "ok")
            if i < len(LABEL_IMAGES_B64):
                time.sleep(1.5)
    else:
        app.log("Printing " + str(len(LABEL_IMAGES_B64)) + " label(s) as single lp job...", "step")
        app.set_status("Printing " + str(len(LABEL_IMAGES_B64)) + " label(s)...")
        print_via_lp(printer_name, LABEL_IMAGES_B64, app.log)
        app.log("All labels sent to printer.", "ok")

    app.log("All " + str(len(LABEL_IMAGES_B64)) + " label(s) printed!", "ok")
    app.set_status("Done – " + str(len(LABEL_IMAGES_B64)) + " label(s) printed.")


if __name__ == "__main__":
    app = App()
    os_note = "Windows GDI" if IS_WINDOWS else ("macOS CUPS (lp)" if IS_MAC else "CUPS (lp)")
    app.log("Ready. Select a printer and click Print Labels.", "info")
    app.log("Prints directly via " + os_note + " — no DYMO software required.", "info")
    app.mainloop()
`;

            const blob = new Blob([pywContent], { type: 'text/plain' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `Bulk_Labels_${bulkBeginDate}_to_${bulkEndDate}.pyw`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error generating bulk thermal label file:', error);
            alert('Failed to generate bulk thermal label file');
        }
        setBulkCombining(false);
    };

    // ---- Download Laser Labels (self-contained HTML) — exact same format as page 3 ----
    const handleBulkDownloadLaserLabels = async () => {
        setBulkCombining(true);
        try {
            const zplText = await fetchBulkZpl();
            if (!zplText) { setBulkCombining(false); return; }

            const dataUrls = await renderBulkZplForLaser(zplText);
            if (!dataUrls) { setBulkCombining(false); return; }

            const labelPages = dataUrls.map((src, idx) => `
                <div class="label-page">
                    <img src="${src}" alt="Label ${idx + 1}" />
                </div>
            `).join('');

            const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <title>Bulk Laser Labels – ${bulkBeginDate} to ${bulkEndDate}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { background: white; }
        body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; padding: 20px; gap: 20px; }
        .toolbar { display: flex; align-items: center; gap: 16px; padding: 16px 24px; background: #fff; border: 2px solid #ff4172; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.12); width: 100%; max-width: 600px; }
        .toolbar h2 { color: #ff4172; font-size: 16px; flex: 1; }
        .toolbar p { color: #666; font-size: 13px; }
        .print-btn { padding: 10px 22px; background: #ff4172; color: white; border: none; border-radius: 5px; font-size: 14px; font-weight: 600; cursor: pointer; flex-shrink: 0; }
        .print-btn:hover { background: #e63361; }
        .label-page { display: flex; justify-content: center; width: 100%; max-width: 600px; border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden; background: white; }
        .label-page img { display: block; width: 100%; height: auto; }
        @media print {
            body { display: block; padding: 0; gap: 0; }
            .toolbar { display: none; }
            .label-page { display: block; width: 100vw; height: 100vh; max-width: none; border: none; border-radius: 0; page-break-before: always; page-break-after: always; page-break-inside: avoid; overflow: hidden; }
            .label-page:first-of-type { page-break-before: avoid; }
            .label-page img { display: block; width: auto; height: 100vh; max-width: 100%; margin: 0 auto; }
        }
    </style>
</head>
<body>
    <div class="toolbar">
        <div>
            <h2>MyCustomFrame — Bulk Laser Labels</h2>
            <p>${bulkBeginDate} to ${bulkEndDate} &nbsp;·&nbsp; ${dataUrls.length} label(s)</p>
        </div>
        <button class="print-btn" onclick="window.print()">🖨️ Print Labels</button>
    </div>
    ${labelPages}
</body>
</html>`;

            const blob = new Blob([htmlContent], { type: 'text/html' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `Bulk_Laser_Labels_${bulkBeginDate}_to_${bulkEndDate}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error generating bulk laser label file:', error);
            alert('Failed to generate bulk laser label file');
        }
        setBulkCombining(false);
    };

    // ---- Thermal Print (live via QZ Tray) — exact same flow as page 3 handlePrintLabel ----
    const handleBulkThermalPrint = async () => {
        setBulkCombining(true);
        try {
            const zplText = await fetchBulkZpl();
            if (!zplText) { setBulkCombining(false); return; }

            const printLabelImages = await renderBulkZplToBase64s(zplText);
            if (!printLabelImages) { setBulkCombining(false); return; }

            const printResponse = await fetch('http://127.0.0.1:9100/print', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ images: printLabelImages })
            });

            const result = await printResponse.json();
            if (!result.ok) throw new Error(result.error || 'Print failed');
            alert(`✓ All ${result.printed} bulk label(s) printed successfully!`);

        } catch (error) {
            console.error('Error bulk thermal printing:', error);
            if (error.message.includes('fetch') || error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                setPrintServerPopup(true);
            } else {
                alert('Failed to print bulk labels.\n\nError: ' + error.message);
            }
        }
        setBulkCombining(false);
    };

    // ---- Laser Print (live via browser iframe) — exact same flow as page 3 handleLaserPrint ----
    const handleBulkLaserPrint = async () => {
        setBulkCombining(true);
        try {
            const zplText = await fetchBulkZpl();
            if (!zplText) { setBulkCombining(false); return; }

            const dataUrls = await renderBulkZplForLaser(zplText);
            if (!dataUrls) { setBulkCombining(false); return; }

            // Convert data URLs → blob URLs for the hidden iframe
            const blobUrls = dataUrls.map(dataUrl => {
                const base64 = dataUrl.split(',')[1];
                const byteChars = atob(base64);
                const byteArr  = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                const blob = new Blob([byteArr], { type: 'image/png' });
                return URL.createObjectURL(blob);
            });

            const labelImgTags = blobUrls.map((src, idx) => `
                <div class="label-page">
                    <img src="${src}" alt="Label ${idx + 1}" />
                </div>
            `).join('');

            const printHTML = `<!DOCTYPE html>
<html>
<head>
    <title>Bulk Labels – ${bulkBeginDate} to ${bulkEndDate}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { height: 100%; background: white; }
        .label-page { display: block; width: 100vw; height: 100vh; page-break-before: always; page-break-after: always; page-break-inside: avoid; overflow: hidden; }
        .label-page:first-child { page-break-before: avoid; }
        .label-page img { display: block; width: auto; height: 100vh; max-width: 100%; margin: 0 auto; }
        @media print {
            * { margin: 0; padding: 0; }
            .label-page { display: block; width: 100vw; height: 100vh; page-break-before: always; page-break-after: always; page-break-inside: avoid; overflow: hidden; }
            .label-page:first-child { page-break-before: avoid; }
            .label-page img { display: block; width: auto; height: 100vh; max-width: 100%; margin: 0 auto; }
        }
    </style>
</head>
<body>
    ${labelImgTags}
</body>
</html>`;

            const htmlBlob   = new Blob([printHTML], { type: 'text/html' });
            const htmlBlobUrl = window.URL.createObjectURL(htmlBlob);

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = htmlBlobUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                setTimeout(() => {
                    try { iframe.contentWindow.print(); } catch (e) { console.error('Print error:', e); }
                }, 500);
            };

            const cleanup = () => {
                window.removeEventListener('focus', cleanup);
                setTimeout(() => {
                    if (iframe.parentNode) document.body.removeChild(iframe);
                    window.URL.revokeObjectURL(htmlBlobUrl);
                    blobUrls.forEach(u => URL.revokeObjectURL(u));
                }, 100);
            };
            window.addEventListener('focus', cleanup);
        } catch (error) {
            console.error('Error bulk laser printing:', error);
            alert('Failed to bulk laser print labels');
        }
        setBulkCombining(false);
    };


    // ============ PAGE 2: EDIT FUNCTIONS ============
    
    const loadProducts = async () => {
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/load-products?orderID=${currentOrderID}`);
            const data = await response.json();
            setProducts(data.products || []);
            setBoxNumbers(data.boxNumbers || []);
            
            const initialSelected = {};
            (data.products || []).forEach(p => {
                initialSelected[p.productNumber] = p.boxSelected;
            });
            setSelectedBoxes(initialSelected);
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    const loadBoxes = async () => {
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/get-boxes?orderID=${currentOrderID}`);
            const data = await response.json();
            setBoxes(data.boxes || []);
            
            const dims = {};
            const editing = {};
            (data.boxes || []).forEach(box => {
                dims[box.boxNumber] = {
                    length: box.length || '',
                    width: box.width || '',
                    height: box.height || '',
                    weight: box.weight || ''
                };
                editing[box.boxNumber] = false;
            });
            setBoxDimensions(dims);
            setEditingBoxes(editing);
        } catch (error) {
            console.error('Error loading boxes:', error);
        }
    };

    useEffect(() => {
        if (currentPage === 'edit' && currentOrderID) {
            loadProducts();
            loadBoxes();
            loadAddressData();
            // Check if this order has previously generated labels
            const checkForExistingLabels = async () => {
                try {
                    const response = await fetch(`${ENDPOINT_PATH}/api/retrieve-labels?orderID=${currentOrderID}`);
                    if (response.ok) {
                        const data = await response.json();
                        setHasExistingLabels(!!(data.combinedLabelUrl));
                    } else {
                        setHasExistingLabels(false);
                    }
                } catch {
                    setHasExistingLabels(false);
                }
            };
            checkForExistingLabels();
        }
    }, [currentPage, currentOrderID]);

    const handleProductBoxChange = (productNumber, newBoxNumber) => {
        setSelectedBoxes(prev => ({
            ...prev,
            [productNumber]: parseInt(newBoxNumber)
        }));
    };

    const saveFilterPreference = async (newFilterType, additionalData = {}) => {
        if (!userUID) return;
        
        let filterSave = '';
        
        if (newFilterType === 'all' || newFilterType === 'unfinished' || newFilterType === 'finished') {
            // Capitalize first letter
            filterSave = newFilterType.charAt(0).toUpperCase() + newFilterType.slice(1);
        } else if (newFilterType === 'dateRange') {
            // Format: DateRange||YYYY-MM-DD||YYYY-MM-DD
            const begin = additionalData.beginDate || dateRangeBegin;
            const end = additionalData.endDate || dateRangeEnd;
            filterSave = `DateRange||${begin}||${end}`;
        } else if (newFilterType === 'search') {
            // Format: Search||searchItem||searchInput
            const item = additionalData.searchItem || searchItem;
            const input = additionalData.searchInput !== undefined ? additionalData.searchInput : searchInput;
            filterSave = `Search||${item}||${input}`;
        }
        
        try {
            await fetch(`${ENDPOINT_PATH}/api/saveUserFilter`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: userUID,
                    filterSave: filterSave
                })
            });
        } catch (error) {
            console.error('Error saving filter preference:', error);
        }
    };

    const handleSaveProductBox = async (productNumber) => {
        const product = products.find(p => p.productNumber === productNumber);
        const newBoxNumber = selectedBoxes[productNumber];
        
        if (product.boxSelected === newBoxNumber) {
            setEditingProducts(prev => ({ ...prev, [productNumber]: false }));
            return;
        }
        
        try {
            await fetch(`${ENDPOINT_PATH}/api/update-product-box-number`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    productNumber: productNumber.toString(),
                    newBoxNumber: newBoxNumber.toString()
                })
            });
            
            setProducts(prev => prev.map(p => 
                p.productNumber === productNumber 
                    ? { ...p, boxSelected: newBoxNumber }
                    : p
            ));
            
            const oldBoxNumber = product.boxSelected;
            setBoxes(prev => prev.map(box => {
                let newAssociated = box.productsAssociated;
                
                if (box.boxNumber === oldBoxNumber && oldBoxNumber > 0) {
                    let list = newAssociated === 'None' ? [] : newAssociated.split(',').map(n => n.trim());
                    list = list.filter(n => n !== productNumber.toString());
                    newAssociated = list.length === 0 ? 'None' : list.join(', ');
                }
                
                if (box.boxNumber === newBoxNumber && newBoxNumber > 0) {
                    let list = newAssociated === 'None' ? [] : newAssociated.split(',').map(n => n.trim());
                    list.push(productNumber.toString());
                    list = [...new Set(list.map(Number))].sort((a, b) => a - b).map(String);
                    newAssociated = list.join(', ');
                }
                
                return { ...box, productsAssociated: newAssociated };
            }));
            
            setEditingProducts(prev => ({ ...prev, [productNumber]: false }));
        } catch (error) {
            console.error('Error updating product box:', error);
            alert('Failed to update product box assignment');
        }
    };

    const handleAddBox = async () => {
        try {
            await fetch(`${ENDPOINT_PATH}/api/add-box`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderID: currentOrderID })
            });
            
            // Refetch boxes from server to get the authoritative state
            const response = await fetch(`${ENDPOINT_PATH}/api/get-boxes?orderID=${currentOrderID}`);
            const data = await response.json();
            const updatedBoxes = data.boxes || [];
            
            setBoxes(updatedBoxes);
            setBoxNumbers(updatedBoxes.map(b => b.boxNumber));
            
            setBoxDimensions(prev => {
                const updated = { ...prev };
                updatedBoxes.forEach(box => {
                    if (!updated[box.boxNumber]) {
                        updated[box.boxNumber] = { length: '', width: '', height: '', weight: '' };
                    }
                });
                return updated;
            });
            
            setEditingBoxes(prev => {
                const updated = { ...prev };
                updatedBoxes.forEach(box => {
                    if (updated[box.boxNumber] === undefined) {
                        updated[box.boxNumber] = true;
                    }
                });
                return updated;
            });
        } catch (error) {
            console.error('Error adding box:', error);
        }
    };

    const handleDeleteBox = async (boxNumber) => {
        if (!window.confirm(`Are you sure you want to delete Box ${boxNumber}?`)) return;
        
        try {
            await fetch(`${ENDPOINT_PATH}/api/delete-box`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    boxNumber: boxNumber.toString()
                })
            });
            
            const deletedBox = boxes.find(b => b.boxNumber === boxNumber);
            let productNumbersToReset = [];
            
            if (deletedBox && deletedBox.productsAssociated !== 'None') {
                productNumbersToReset = deletedBox.productsAssociated.split(',').map(n => parseInt(n.trim()));
            }
            
            setProducts(prev => prev.map(p => {
                if (productNumbersToReset.includes(p.productNumber)) {
                    return { ...p, boxSelected: 0 };
                }
                return p;
            }));
            
            setSelectedBoxes(prev => {
                const updated = { ...prev };
                productNumbersToReset.forEach(pNum => {
                    updated[pNum] = 0;
                });
                return updated;
            });
            
            const remainingBoxes = boxes.filter(b => b.boxNumber !== boxNumber);
            const renumberedBoxes = remainingBoxes.map((b, index) => ({
                ...b,
                boxNumber: index + 1
            }));
            
            setBoxes(renumberedBoxes);
            setBoxNumbers(renumberedBoxes.map(b => b.boxNumber));
            
            const newDimensions = {};
            const newEditing = {};
            renumberedBoxes.forEach((box, index) => {
                const oldBoxNum = remainingBoxes[index].boxNumber;
                const newBoxNum = box.boxNumber;
                newDimensions[newBoxNum] = boxDimensions[oldBoxNum];
                newEditing[newBoxNum] = editingBoxes[oldBoxNum];
            });
            setBoxDimensions(newDimensions);
            setEditingBoxes(newEditing);

            const newCheckboxes = {};
            renumberedBoxes.forEach((box, index) => {
                const oldBoxNum = remainingBoxes[index].boxNumber;
                newCheckboxes[box.boxNumber] = selectedBoxCheckboxes[oldBoxNum] || false;
            });
            setSelectedBoxCheckboxes(newCheckboxes);
            
            setProducts(prev => prev.map(p => {
                const oldBoxSelected = p.boxSelected;
                if (oldBoxSelected > 0) {
                    const oldBox = remainingBoxes.find(b => b.boxNumber === oldBoxSelected);
                    if (oldBox) {
                        const newBoxNum = renumberedBoxes.find(b => 
                            b.productsAssociated === oldBox.productsAssociated
                        )?.boxNumber;
                        if (newBoxNum) {
                            return { ...p, boxSelected: newBoxNum };
                        }
                    }
                }
                return p;
            }));
        } catch (error) {
            console.error('Error deleting box:', error);
        }
    };

    const handleBoxDimensionChange = (boxNumber, field, value) => {
        setBoxDimensions(prev => ({
            ...prev,
            [boxNumber]: {
                ...prev[boxNumber],
                [field]: value
            }
        }));
    };

    const handlePresetChange = (boxNumber, preset) => {
        if (preset === 'custom' || preset === 'select') {
            setBoxDimensions(prev => ({
                ...prev,
                [boxNumber]: { length: '', width: '', height: '', weight: '' }
            }));
        } else {
            setBoxDimensions(prev => ({
                ...prev,
                [boxNumber]: { ...presetDimensions[preset] }
            }));
        }
    };

    const getSelectedPreset = (boxNumber) => {
        const dims = boxDimensions[boxNumber];
        if (!dims) return 'select';
        
        for (const [key, value] of Object.entries(presetDimensions)) {
            if (dims.length === value.length && 
                dims.width === value.width && 
                dims.height === value.height && 
                dims.weight === value.weight) {
                return key;
            }
        }
        
        if (dims.length || dims.width || dims.height || dims.weight) {
            return 'custom';
        }
        
        return 'select';
    };

    const handleSaveBox = async (boxNumber) => {
        const dims = boxDimensions[boxNumber];
        
        // Validate that all non-empty values are numbers
        const invalidFields = [];
        if (dims.length && isNaN(dims.length)) invalidFields.push('Length');
        if (dims.width && isNaN(dims.width)) invalidFields.push('Width');
        if (dims.height && isNaN(dims.height)) invalidFields.push('Height');
        if (dims.weight && isNaN(dims.weight)) invalidFields.push('Weight');
        
        if (invalidFields.length > 0) {
            alert(`Box ${boxNumber} has invalid inputs: ${invalidFields.join(', ')} must be numbers only`);
            return;
        }
        
        try {
            await fetch(`${ENDPOINT_PATH}/api/update-box`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    boxNumber: boxNumber.toString(),
                    length: dims.length || null,
                    width: dims.width || null,
                    height: dims.height || null,
                    weight: dims.weight || null
                })
            });
            
            setBoxes(prev => prev.map(box => 
                box.boxNumber === boxNumber
                    ? { ...box, ...dims }
                    : box
            ));
            
            setEditingBoxes(prev => ({ ...prev, [boxNumber]: false }));
        } catch (error) {
            console.error('Error saving box:', error);
            alert('Failed to save box dimensions');
        }
    };

    const handleAutofill = async () => {
        const checkedBoxes = Object.entries(autofillChecked)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum));
        
        if (checkedBoxes.length === 0) {
            alert('Please select at least one box to autofill');
            return;
        }
        
        const boxesWithNoProducts = [];
        checkedBoxes.forEach(boxNum => {
            const box = boxes.find(b => b.boxNumber === boxNum);
            if (!box || box.productsAssociated === 'None') {
                boxesWithNoProducts.push(boxNum);
            }
        });
        
        if (boxesWithNoProducts.length > 0) {
            alert(`Box(es) ${boxesWithNoProducts.join(', ')} need at least one product to autofill`);
            return;
        }
        
        const boxesToUpdate = [];
        
        checkedBoxes.forEach(boxNum => {
            const box = boxes.find(b => b.boxNumber === boxNum);
            const productNums = box.productsAssociated.split(',').map(n => parseInt(n.trim()));
            
            let maxWidth = 0, maxHeight = 0;
            let totalHeight = 0, totalWeight = 0;
            productNums.forEach(pNum => {
                const product = products.find(p => p.productNumber === pNum);
                if (product && product.imageDimensions) {
                    maxWidth = Math.max(maxWidth, product.imageDimensions.width);
                    maxHeight = Math.max(maxHeight, product.imageDimensions.height);
                    // Sum height and weight from each product's preset
                    const pSizeKey = `${product.imageDimensions.width}x${product.imageDimensions.height}`;
                    const pPresetKey = imageSizeToBox[pSizeKey];
                    if (pPresetKey && presetDimensions[pPresetKey]) {
                        totalHeight += parseFloat(presetDimensions[pPresetKey].height);
                        totalWeight += parseFloat(presetDimensions[pPresetKey].weight);
                    }
                }
            });
            
            const sizeKey = `${maxWidth}x${maxHeight}`;
            const boxPreset = imageSizeToBox[sizeKey];
            
            if (boxPreset && presetDimensions[boxPreset]) {
                const basePreset = presetDimensions[boxPreset];
                const newDims = {
                    length: basePreset.length,
                    width: basePreset.width,
                    height: String(totalHeight),
                    weight: String(totalWeight)
                };
                
                setBoxDimensions(prev => ({
                    ...prev,
                    [boxNum]: { ...newDims }
                }));
                
                if (!editingBoxes[boxNum]) {
                    const currentBox = boxes.find(b => b.boxNumber === boxNum);
                    if (currentBox.length !== newDims.length ||
                        currentBox.width !== newDims.width ||
                        currentBox.height !== newDims.height ||
                        currentBox.weight !== newDims.weight) {
                        boxesToUpdate.push({
                            boxNumber: boxNum.toString(),
                            length: newDims.length,
                            width: newDims.width,
                            height: newDims.height,
                            weight: newDims.weight
                        });
                    }
                    
                    setBoxes(prev => prev.map(box => 
                        box.boxNumber === boxNum
                            ? { ...box, ...newDims }
                            : box
                    ));
                }
            }
        });
        
        if (boxesToUpdate.length > 0) {
            try {
                await fetch(`${ENDPOINT_PATH}/api/autofill-boxes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        orderID: currentOrderID,
                        boxes: boxesToUpdate
                    })
                });
            } catch (error) {
                console.error('Error autofilling boxes:', error);
            }
        }
        
        setAutofillChecked({});
    };

    const handleAssignProductsToBoxes = async () => {
        const perBox = parseInt(productsPerBoxAssign);

        if (!productsPerBoxAssign || isNaN(perBox) || perBox < 1) {
            alert('Please enter a valid integer (1 or more) for products per box.');
            return;
        }

        const totalProducts = products.length;
        if (totalProducts === 0) {
            alert('No products found in this order.');
            return;
        }

        const boxesNeeded = Math.ceil(totalProducts / perBox);

        if (boxesNeeded > boxes.length) {
            const moreNeeded = boxesNeeded - boxes.length;
            alert(`Not enough boxes. You need ${moreNeeded} more box${moreNeeded > 1 ? 'es' : ''} (currently ${boxes.length}, need ${boxesNeeded}).`);
            return;
        }

        setAssignWarning('');

        const sortedProducts = [...products].sort((a, b) => a.productNumber - b.productNumber);

        const assignments = sortedProducts.map((product, index) => ({
            productNumber: product.productNumber,
            boxNumber: Math.floor(index / perBox) + 1
        }));

        try {
            await fetch(`${ENDPOINT_PATH}/api/bulk-assign-products-to-boxes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    assignments: assignments
                })
            });

            setProducts(prev => prev.map(p => {
                const assignment = assignments.find(a => a.productNumber === p.productNumber);
                if (assignment) {
                    return { ...p, boxSelected: assignment.boxNumber };
                }
                return p;
            }));

            setSelectedBoxes(prev => {
                const updated = { ...prev };
                assignments.forEach(a => {
                    updated[a.productNumber] = a.boxNumber;
                });
                return updated;
            });

            const boxProductMap = {};
            boxes.forEach(b => {
                boxProductMap[b.boxNumber] = [];
            });
            assignments.forEach(a => {
                if (boxProductMap[a.boxNumber] !== undefined) {
                    boxProductMap[a.boxNumber].push(a.productNumber);
                }
            });

            setBoxes(prev => prev.map(box => {
                const productList = boxProductMap[box.boxNumber] || [];
                return {
                    ...box,
                    productsAssociated: productList.length === 0
                        ? 'None'
                        : productList.sort((a, b) => a - b).join(', ')
                };
            }));

        } catch (error) {
            console.error('Error assigning products to boxes:', error);
            alert('Failed to assign products to boxes.');
        }
    };

    const handleAutoCompleteOrder = async () => {
        const perBox = parseInt(productsPerBoxAuto);

        if (!productsPerBoxAuto || isNaN(perBox) || perBox < 1) {
            alert('Please enter a valid integer (1 or more) for products per box.');
            return;
        }

        const totalProducts = products.length;
        if (totalProducts === 0) {
            alert('No products found in this order.');
            return;
        }

        if (perBox > totalProducts) {
            alert(`Products per box (${perBox}) cannot be greater than the total number of products in the order (${totalProducts}).`);
            return;
        }

        const boxesNeeded = Math.ceil(totalProducts / perBox);

        const confirmMessage = boxes.length > 0
            ? `This will DELETE all ${boxes.length} existing box(es) and recreate ${boxesNeeded} box(es) based on ${perBox} product(s) per box. This cannot be undone. Continue?`
            : `This will create ${boxesNeeded} box(es) based on ${perBox} product(s) per box. Continue?`;

        if (!window.confirm(confirmMessage)) return;

        setAssignWarning('');

        try {
            // Step 1: Delete all existing boxes if any
            if (boxes.length > 0) {
                const existingBoxNumbers = boxes.map(b => b.boxNumber);
                await fetch(`${ENDPOINT_PATH}/api/delete-multiple-boxes`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderID: currentOrderID, boxNumbers: existingBoxNumbers })
                });
            }

            // Step 2: Bulk add all needed boxes in one request
            await fetch(`${ENDPOINT_PATH}/api/bulk-add-boxes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderID: currentOrderID, count: boxesNeeded })
            });

            const newBoxes = Array.from({ length: boxesNeeded }, (_, i) => ({
                boxNumber: i + 1,
                length: null,
                width: null,
                height: null,
                weight: null,
                productsAssociated: 'None'
            }));

            setBoxes(newBoxes);
            setBoxNumbers(newBoxes.map(b => b.boxNumber));

            const newDimensionsState = {};
            const newEditingState = {};
            newBoxes.forEach(b => {
                newDimensionsState[b.boxNumber] = { length: '', width: '', height: '', weight: '' };
                newEditingState[b.boxNumber] = false;
            });
            setBoxDimensions(newDimensionsState);
            setEditingBoxes(newEditingState);
            setSelectedBoxCheckboxes({});

            // Step 3: Assign products to boxes
            const sortedProducts = [...products].sort((a, b) => a.productNumber - b.productNumber);
            const assignments = sortedProducts.map((product, index) => ({
                productNumber: product.productNumber,
                boxNumber: Math.floor(index / perBox) + 1
            }));

            await fetch(`${ENDPOINT_PATH}/api/bulk-assign-products-to-boxes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderID: currentOrderID, assignments })
            });

            const updatedProducts = products.map(p => {
                const assignment = assignments.find(a => a.productNumber === p.productNumber);
                return assignment ? { ...p, boxSelected: assignment.boxNumber } : p;
            });
            setProducts(updatedProducts);

            const updatedSelectedBoxes = {};
            assignments.forEach(a => { updatedSelectedBoxes[a.productNumber] = a.boxNumber; });
            setSelectedBoxes(updatedSelectedBoxes);

            const boxProductMap = {};
            newBoxes.forEach(b => { boxProductMap[b.boxNumber] = []; });
            assignments.forEach(a => {
                if (boxProductMap[a.boxNumber] !== undefined) {
                    boxProductMap[a.boxNumber].push(a.productNumber);
                }
            });

            const updatedBoxes = newBoxes.map(box => ({
                ...box,
                productsAssociated: (boxProductMap[box.boxNumber] || []).length === 0
                    ? 'None'
                    : (boxProductMap[box.boxNumber] || []).sort((a, b) => a - b).join(', ')
            }));
            setBoxes(updatedBoxes);

            // Step 4: Autofill all boxes
            const boxesToUpdate = [];
            const newDimensions = {};

            updatedBoxes.forEach(box => {
                if (box.productsAssociated === 'None') return;

                const productNums = box.productsAssociated.split(',').map(n => parseInt(n.trim()));
                let maxWidth = 0, maxHeight = 0, totalHeight = 0, totalWeight = 0;

                productNums.forEach(pNum => {
                    const product = updatedProducts.find(p => p.productNumber === pNum);
                    if (product && product.imageDimensions) {
                        maxWidth = Math.max(maxWidth, product.imageDimensions.width);
                        maxHeight = Math.max(maxHeight, product.imageDimensions.height);
                        const pSizeKey = `${product.imageDimensions.width}x${product.imageDimensions.height}`;
                        const pPresetKey = imageSizeToBox[pSizeKey];
                        if (pPresetKey && presetDimensions[pPresetKey]) {
                            totalHeight += parseFloat(presetDimensions[pPresetKey].height);
                            totalWeight += parseFloat(presetDimensions[pPresetKey].weight);
                        }
                    }
                });

                const sizeKey = `${maxWidth}x${maxHeight}`;
                const boxPreset = imageSizeToBox[sizeKey];

                if (boxPreset && presetDimensions[boxPreset]) {
                    const basePreset = presetDimensions[boxPreset];
                    const newDims = {
                        length: basePreset.length,
                        width: basePreset.width,
                        height: String(totalHeight),
                        weight: String(totalWeight)
                    };
                    newDimensions[box.boxNumber] = newDims;
                    boxesToUpdate.push({
                        boxNumber: box.boxNumber.toString(),
                        ...newDims
                    });
                }
            });

            if (boxesToUpdate.length > 0) {
                await fetch(`${ENDPOINT_PATH}/api/autofill-boxes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderID: currentOrderID, boxes: boxesToUpdate })
                });

                setBoxDimensions(newDimensions);
                setBoxes(prev => prev.map(box => {
                    const dims = newDimensions[box.boxNumber];
                    return dims ? { ...box, ...dims } : box;
                }));
            }

            // Step 5: Bulk save all boxes
            const boxesDataToSave = updatedBoxes.map(box => {
                const dims = newDimensions[box.boxNumber] || {};
                return {
                    boxNumber: box.boxNumber.toString(),
                    length: dims.length || null,
                    width: dims.width || null,
                    height: dims.height || null,
                    weight: dims.weight || null
                };
            });

            await fetch(`${ENDPOINT_PATH}/api/bulk-save-boxes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderID: currentOrderID, boxes: boxesDataToSave })
            });

            const finalEditing = {};
            updatedBoxes.forEach(b => { finalEditing[b.boxNumber] = false; });
            setEditingBoxes(finalEditing);

            alert(`Auto Complete finished! ${boxesNeeded} box(es) created, products assigned, boxes autofilled, and changes saved.`);

        } catch (error) {
            console.error('Error in Auto Complete:', error);
            alert('Auto Complete failed. Please try again.');
        }
    };

    const handleGenerateLabels = async () => {
        let warnings = [];
        let mandatoryWarnings = [];

        if (editingAddress) {
            mandatoryWarnings.push('Address is in edit mode. Please save the address before generating labels.');
        }
        
        if (boxes.length === 0) {
            mandatoryWarnings.push('No boxes in order. Cannot generate labels without boxes.');
        }

        Object.entries(editingProducts).forEach(([pNum, editing]) => {
            if (editing) {
                mandatoryWarnings.push(`Product ${pNum} is in edit mode. Please save before generating labels.`);
            }
        });
        
        Object.entries(editingBoxes).forEach(([bNum, editing]) => {
            if (editing) {
                mandatoryWarnings.push(`Box ${bNum} is in edit mode. Please save before generating labels.`);
            }
        });
        
        boxes.forEach(box => {
            const dims = boxDimensions[box.boxNumber] || box;
            if (!dims.length || !dims.width || !dims.height || !dims.weight) {
                mandatoryWarnings.push(`Box ${box.boxNumber} is missing dimensions.`);
            }
        });
        
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/check-products?orderID=${currentOrderID}`);
            const data = await response.json();
            if (data.message && !data.message.includes('All products')) {
                warnings.push(data.message);
            }
        } catch (error) {
            console.error('Error checking products:', error);
        }
        
        boxes.forEach(box => {
            if (box.productsAssociated !== 'None') {
                const productNums = box.productsAssociated.split(',').map(n => parseInt(n.trim()));
                const dims = boxDimensions[box.boxNumber] || box;
                
                const boxPreset = Object.entries(presetDimensions).find(([key, value]) => 
                    value.length === dims.length && 
                    value.width === dims.width && 
                    value.height === dims.height && 
                    value.weight === dims.weight
                );
                
                const boxKey = boxPreset ? boxPreset[0] : null;
                const maxAllowedSizes = boxKey ? Object.entries(imageSizeToBox)
                    .filter(([_, val]) => val === boxKey)
                    .map(([key, _]) => key) : [];
                
                productNums.forEach(pNum => {
                    const product = products.find(p => p.productNumber === pNum);
                    if (product && product.imageDimensions) {
                        const productSize = `${product.imageDimensions.width}x${product.imageDimensions.height}`;
                        const productBoxKey = imageSizeToBox[productSize];
                        
                        if (productBoxKey) {
                            const productPreset = presetDimensions[productBoxKey];
                            const currentPreset = presetDimensions[boxKey] || dims;
                            
                            if (parseInt(productPreset.length) > parseInt(currentPreset.length) ||
                                parseInt(productPreset.width) > parseInt(currentPreset.width)) {
                                warnings.push(`Product ${pNum} (${productSize}) may not fit in Box ${box.boxNumber}`);
                            }
                        }
                    }
                });
            }
        });
        
        const allWarnings = [...mandatoryWarnings, ...warnings];
        
        if (allWarnings.length > 0) {
            const message = allWarnings.join('\n\n');
            const hasMandatory = mandatoryWarnings.length > 0;
            
            if (hasMandatory) {
                alert(message);
                return;
            } else {
                if (!window.confirm(message + '\n\nContinue anyway?')) {
                    return;
                }
            }
        }
        
        setLoading(true);
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/generate-labels`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderID: currentOrderID })
            });
            
            if (response.ok) {
                setLabelImages([]);
                setCurrentPage('view');
            } else {
                const error = await response.json();
                alert(`Error generating labels: ${error.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error generating labels:', error);
            alert('Failed to generate labels');
        }
        setLoading(false);
    };


    // Add handler for checkbox selection
    const handleBoxCheckboxChange = (boxNumber, checked) => {
        setSelectedBoxCheckboxes(prev => ({
            ...prev,
            [boxNumber]: checked
        }));
    };

    // Add handler for select all checkboxes
    const handleSelectAllBoxes = (checked) => {
        const newSelected = {};
        boxes.forEach(box => {
            newSelected[box.boxNumber] = checked;
        });
        setSelectedBoxCheckboxes(newSelected);
    };

    // Add handler for bulk edit mode
    const handleBulkEditMode = () => {
        const selectedBoxNums = Object.entries(selectedBoxCheckboxes)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum));
        
        if (selectedBoxNums.length === 0) {
            alert('Please select at least one box');
            return;
        }
        
        const newEditing = { ...editingBoxes };
        selectedBoxNums.forEach(boxNum => {
            newEditing[boxNum] = true;
        });
        setEditingBoxes(newEditing);
    };

    // Add handler for bulk save mode
    const handleBulkSaveMode = async () => {
        const selectedBoxNums = Object.entries(selectedBoxCheckboxes)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum));
        
        if (selectedBoxNums.length === 0) {
            alert('Please select at least one box');
            return;
        }
        
        // Filter only boxes that are in edit mode
        const boxesToSave = selectedBoxNums.filter(boxNum => editingBoxes[boxNum]);
        
        if (boxesToSave.length === 0) {
            alert('No selected boxes are in edit mode');
            return;
        }
        
        // Validate dimensions before saving
        const invalidBoxes = [];
        boxesToSave.forEach(boxNum => {
            const dims = boxDimensions[boxNum];
            if (dims.length && isNaN(dims.length)) invalidBoxes.push(`Box ${boxNum}: Length`);
            if (dims.width && isNaN(dims.width)) invalidBoxes.push(`Box ${boxNum}: Width`);
            if (dims.height && isNaN(dims.height)) invalidBoxes.push(`Box ${boxNum}: Height`);
            if (dims.weight && isNaN(dims.weight)) invalidBoxes.push(`Box ${boxNum}: Weight`);
        });
        
        if (invalidBoxes.length > 0) {
            alert(`Invalid inputs detected:\n${invalidBoxes.join('\n')}\nAll dimensions must be numbers only`);
            return;
        }
        
        // Prepare boxes data for bulk save
        const boxesData = boxesToSave.map(boxNum => {
            const dims = boxDimensions[boxNum];
            return {
                boxNumber: boxNum.toString(),
                length: dims.length || null,
                width: dims.width || null,
                height: dims.height || null,
                weight: dims.weight || null
            };
        });
        
        try {
            await fetch(`${ENDPOINT_PATH}/api/bulk-save-boxes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    boxes: boxesData
                })
            });
            
            // Update local state for all saved boxes
            setBoxes(prev => prev.map(box => {
                if (boxesToSave.includes(box.boxNumber)) {
                    const dims = boxDimensions[box.boxNumber];
                    return { ...box, ...dims };
                }
                return box;
            }));
            
            // Set all saved boxes to non-editing mode
            const newEditing = { ...editingBoxes };
            boxesToSave.forEach(boxNum => {
                newEditing[boxNum] = false;
            });
            setEditingBoxes(newEditing);
            
        } catch (error) {
            console.error('Error saving boxes:', error);
            alert('Failed to save boxes');
        }
    };

    // Add handler for bulk delete
    const handleBulkDelete = async () => {
        const selectedBoxNums = Object.entries(selectedBoxCheckboxes)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum));
        
        if (selectedBoxNums.length === 0) {
            alert('Please select at least one box to delete');
            return;
        }
        
        const boxList = selectedBoxNums.join(', ');
        if (!window.confirm(`Are you sure you want to delete Box(es) ${boxList}?`)) return;
        
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/delete-multiple-boxes`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    boxNumbers: selectedBoxNums
                })
            });
            
            const result = await response.json();
            
            if (!response.ok) {
                throw new Error(result);
            }
            
            // Update local state based on server response
            // Get boxes that were deleted
            const deletedBoxes = boxes.filter(b => selectedBoxNums.includes(b.boxNumber));
            
            // Collect product numbers to reset
            let productNumbersToReset = [];
            deletedBoxes.forEach(box => {
                if (box.productsAssociated !== 'None') {
                    productNumbersToReset.push(...box.productsAssociated.split(',').map(n => parseInt(n.trim())));
                }
            });
            
            // Reset products
            setProducts(prev => prev.map(p => {
                if (productNumbersToReset.includes(p.productNumber)) {
                    return { ...p, boxSelected: 0 };
                }
                return p;
            }));
            
            setSelectedBoxes(prev => {
                const updated = { ...prev };
                productNumbersToReset.forEach(pNum => {
                    updated[pNum] = 0;
                });
                return updated;
            });
            
            // Remove deleted boxes and renumber
            const remainingBoxes = boxes.filter(b => !selectedBoxNums.includes(b.boxNumber));
            const renumberedBoxes = remainingBoxes.map((b, index) => ({
                ...b,
                boxNumber: index + 1
            }));
            
            setBoxes(renumberedBoxes);
            setBoxNumbers(renumberedBoxes.map(b => b.boxNumber));
            
            // Update dimensions and editing state
            const newDimensions = {};
            const newEditing = {};
            renumberedBoxes.forEach((box, index) => {
                const oldBoxNum = remainingBoxes[index].boxNumber;
                const newBoxNum = box.boxNumber;
                newDimensions[newBoxNum] = boxDimensions[oldBoxNum];
                newEditing[newBoxNum] = editingBoxes[oldBoxNum];
            });
            setBoxDimensions(newDimensions);
            setEditingBoxes(newEditing);

            const newCheckboxes = {};
            renumberedBoxes.forEach((box, index) => {
                const oldBoxNum = remainingBoxes[index].boxNumber;
                newCheckboxes[box.boxNumber] = selectedBoxCheckboxes[oldBoxNum] || false;
            });
            setSelectedBoxCheckboxes(newCheckboxes);
            
            // Update products with new box numbers
            setProducts(prev => prev.map(p => {
                const oldBoxSelected = p.boxSelected;
                if (oldBoxSelected > 0) {
                    const oldBox = remainingBoxes.find(b => b.boxNumber === oldBoxSelected);
                    if (oldBox) {
                        const newBoxNum = renumberedBoxes.find(b => 
                            b.productsAssociated === oldBox.productsAssociated
                        )?.boxNumber;
                        if (newBoxNum) {
                            return { ...p, boxSelected: newBoxNum };
                        }
                    }
                }
                return p;
            }));
            
            // Clear selections
            setSelectedBoxCheckboxes({});
            
        } catch (error) {
            console.error('Error deleting boxes:', error);
            alert('Failed to delete boxes');
        }
    };

    // Add handler for bulk autofill using existing checkbox system
    const handleBulkCopy = async () => {
        const selectedBoxNums = Object.entries(selectedBoxCheckboxes)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum));

        if (selectedBoxNums.length === 0) {
            alert('Please select at least one box');
            return;
        }

        if (!bulkCopyDims.length && !bulkCopyDims.width && !bulkCopyDims.height && !bulkCopyDims.weight) {
            alert('Please enter at least one dimension to copy');
            return;
        }

        const invalidFields = [];
        if (bulkCopyDims.length && isNaN(bulkCopyDims.length)) invalidFields.push('Length');
        if (bulkCopyDims.width && isNaN(bulkCopyDims.width)) invalidFields.push('Width');
        if (bulkCopyDims.height && isNaN(bulkCopyDims.height)) invalidFields.push('Height');
        if (bulkCopyDims.weight && isNaN(bulkCopyDims.weight)) invalidFields.push('Weight');

        if (invalidFields.length > 0) {
            alert(`Invalid inputs: ${invalidFields.join(', ')} must be numbers only`);
            return;
        }

        const boxesToSaveToDb = [];

        selectedBoxNums.forEach(boxNum => {
            const currentDims = boxDimensions[boxNum] || {};
            const newDims = {
                length: bulkCopyDims.length || currentDims.length || '',
                width: bulkCopyDims.width || currentDims.width || '',
                height: bulkCopyDims.height || currentDims.height || '',
                weight: bulkCopyDims.weight || currentDims.weight || ''
            };

            setBoxDimensions(prev => ({ ...prev, [boxNum]: newDims }));

            if (!editingBoxes[boxNum]) {
                setBoxes(prev => prev.map(box =>
                    box.boxNumber === boxNum ? { ...box, ...newDims } : box
                ));
                boxesToSaveToDb.push({
                    boxNumber: boxNum.toString(),
                    length: newDims.length || null,
                    width: newDims.width || null,
                    height: newDims.height || null,
                    weight: newDims.weight || null
                });
            }
        });

        if (boxesToSaveToDb.length > 0) {
            try {
                await fetch(`${ENDPOINT_PATH}/api/bulk-save-boxes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        orderID: currentOrderID,
                        boxes: boxesToSaveToDb
                    })
                });
            } catch (error) {
                console.error('Error bulk copying box dimensions:', error);
                alert('Failed to save copied dimensions');
            }
        }
    };

    const handleCopyBoxToSelected = async (boxNumber) => {
        const selectedBoxNums = Object.entries(selectedBoxCheckboxes)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum))
            .filter(num => num !== boxNumber);

        if (selectedBoxNums.length === 0) {
            alert('Please select at least one other box to copy to');
            return;
        }

        const sourceDims = boxDimensions[boxNumber];
        if (!sourceDims || (!sourceDims.length && !sourceDims.width && !sourceDims.height && !sourceDims.weight)) {
            alert(`Box ${boxNumber} has no dimensions to copy`);
            return;
        }

        const newDims = {
            length: sourceDims.length || '',
            width: sourceDims.width || '',
            height: sourceDims.height || '',
            weight: sourceDims.weight || ''
        };

        const boxesToSaveToDb = [];

        selectedBoxNums.forEach(targetBoxNum => {
            setBoxDimensions(prev => ({ ...prev, [targetBoxNum]: { ...newDims } }));

            if (!editingBoxes[targetBoxNum]) {
                setBoxes(prev => prev.map(box =>
                    box.boxNumber === targetBoxNum ? { ...box, ...newDims } : box
                ));
                boxesToSaveToDb.push({
                    boxNumber: targetBoxNum.toString(),
                    length: newDims.length || null,
                    width: newDims.width || null,
                    height: newDims.height || null,
                    weight: newDims.weight || null
                });
            }
        });

        if (boxesToSaveToDb.length > 0) {
            try {
                await fetch(`${ENDPOINT_PATH}/api/bulk-save-boxes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        orderID: currentOrderID,
                        boxes: boxesToSaveToDb
                    })
                });
            } catch (error) {
                console.error('Error copying box dimensions:', error);
                alert('Failed to save copied dimensions');
            }
        }
    };

    const handleSingleBoxAutofill = async (boxNumber) => {
        const box = boxes.find(b => b.boxNumber === boxNumber);
        if (!box || box.productsAssociated === 'None') {
            alert(`Box ${boxNumber} needs at least one product to autofill`);
            return;
        }

        const productNums = box.productsAssociated.split(',').map(n => parseInt(n.trim()));
        let maxWidth = 0, maxHeight = 0, totalHeight = 0, totalWeight = 0;

        productNums.forEach(pNum => {
            const product = products.find(p => p.productNumber === pNum);
            if (product && product.imageDimensions) {
                maxWidth = Math.max(maxWidth, product.imageDimensions.width);
                maxHeight = Math.max(maxHeight, product.imageDimensions.height);
                const pSizeKey = `${product.imageDimensions.width}x${product.imageDimensions.height}`;
                const pPresetKey = imageSizeToBox[pSizeKey];
                if (pPresetKey && presetDimensions[pPresetKey]) {
                    totalHeight += parseFloat(presetDimensions[pPresetKey].height);
                    totalWeight += parseFloat(presetDimensions[pPresetKey].weight);
                }
            }
        });

        const sizeKey = `${maxWidth}x${maxHeight}`;
        const boxPreset = imageSizeToBox[sizeKey];

        if (!boxPreset || !presetDimensions[boxPreset]) {
            alert(`Could not determine dimensions for Box ${boxNumber}. Please check the products assigned to it.`);
            return;
        }

        const basePreset = presetDimensions[boxPreset];
        const newDims = {
            length: basePreset.length,
            width: basePreset.width,
            height: String(totalHeight),
            weight: String(totalWeight)
        };

        setBoxDimensions(prev => ({ ...prev, [boxNumber]: { ...newDims } }));
        setBoxes(prev => prev.map(b => b.boxNumber === boxNumber ? { ...b, ...newDims } : b));

        try {
            await fetch(`${ENDPOINT_PATH}/api/autofill-boxes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderID: currentOrderID,
                    boxes: [{ boxNumber: boxNumber.toString(), ...newDims }]
                })
            });
        } catch (error) {
            console.error('Error autofilling box:', error);
        }
    };

    const handleBulkAutofill = async () => {
        const selectedBoxNums = Object.entries(selectedBoxCheckboxes)
            .filter(([_, checked]) => checked)
            .map(([boxNum, _]) => parseInt(boxNum));
        
        if (selectedBoxNums.length === 0) {
            alert('Please select at least one box to autofill');
            return;
        }
        
        const boxesWithNoProducts = [];
        selectedBoxNums.forEach(boxNum => {
            const box = boxes.find(b => b.boxNumber === boxNum);
            if (!box || box.productsAssociated === 'None') {
                boxesWithNoProducts.push(boxNum);
            }
        });
        
        if (boxesWithNoProducts.length > 0) {
            alert(`Box(es) ${boxesWithNoProducts.join(', ')} need at least one product to autofill`);
            return;
        }
        
        const boxesToUpdate = [];
        
        selectedBoxNums.forEach(boxNum => {
            const box = boxes.find(b => b.boxNumber === boxNum);
            const productNums = box.productsAssociated.split(',').map(n => parseInt(n.trim()));
            
            let maxWidth = 0, maxHeight = 0;
            let totalHeight = 0, totalWeight = 0;
            productNums.forEach(pNum => {
                const product = products.find(p => p.productNumber === pNum);
                if (product && product.imageDimensions) {
                    maxWidth = Math.max(maxWidth, product.imageDimensions.width);
                    maxHeight = Math.max(maxHeight, product.imageDimensions.height);
                    // Sum height and weight from each product's preset
                    const pSizeKey = `${product.imageDimensions.width}x${product.imageDimensions.height}`;
                    const pPresetKey = imageSizeToBox[pSizeKey];
                    if (pPresetKey && presetDimensions[pPresetKey]) {
                        totalHeight += parseFloat(presetDimensions[pPresetKey].height);
                        totalWeight += parseFloat(presetDimensions[pPresetKey].weight);
                    }
                }
            });
            
            const sizeKey = `${maxWidth}x${maxHeight}`;
            const boxPreset = imageSizeToBox[sizeKey];
            
            if (boxPreset && presetDimensions[boxPreset]) {
                const basePreset = presetDimensions[boxPreset];
                const newDims = {
                    length: basePreset.length,
                    width: basePreset.width,
                    height: String(totalHeight),
                    weight: String(totalWeight)
                };
                
                setBoxDimensions(prev => ({
                    ...prev,
                    [boxNum]: { ...newDims }
                }));
                
                if (!editingBoxes[boxNum]) {
                    const currentBox = boxes.find(b => b.boxNumber === boxNum);
                    if (currentBox.length !== newDims.length ||
                        currentBox.width !== newDims.width ||
                        currentBox.height !== newDims.height ||
                        currentBox.weight !== newDims.weight) {
                        boxesToUpdate.push({
                            boxNumber: boxNum.toString(),
                            length: newDims.length,
                            width: newDims.width,
                            height: newDims.height,
                            weight: newDims.weight
                        });
                    }
                    
                    setBoxes(prev => prev.map(box => 
                        box.boxNumber === boxNum
                            ? { ...box, ...newDims }
                            : box
                    ));
                }
            }
        });
        
        if (boxesToUpdate.length > 0) {
            try {
                await fetch(`${ENDPOINT_PATH}/api/autofill-boxes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        orderID: currentOrderID,
                        boxes: boxesToUpdate
                    })
                });
            } catch (error) {
                console.error('Error autofilling boxes:', error);
            }
        }
    };

    // ============ PAGE 3: VIEW FUNCTIONS ============
    
    const loadLabels = async () => {
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/retrieve-labels?orderID=${currentOrderID}`);
            const data = await response.json();
            setCombinedLabelUrl(data.combinedLabelUrl || '');
            setTotalLabelPrice(data.totalLabelPrice || '0.00');
            setFinalizedBoxes(data.finalizedBoxes || []);
            if (data.labelGenerationTime) {
                setOrderInfo(prev => ({ ...prev, labelGenerationTime: data.labelGenerationTime }));
            }
            if (data.finalizedAddress1 !== undefined) {
                setAddressFields({
                    shippingFirstName: data.finalizedShippingFirstName || '',
                    shippingLastName: data.finalizedShippingLastName || '',
                    address1: data.finalizedAddress1 || '',
                    address2: data.finalizedAddress2 || '',
                    city: data.finalizedCity || '',
                    postalCode: data.finalizedPostalCode || '',
                    state: data.finalizedState || ''
                });
            }

            // Fetch ZPL file and render each label as PNG via Labelary
            if (data.combinedLabelUrl) {
                try {
                    const proxyRes = await fetch(`${ENDPOINT_PATH}/api/proxy-label-file?url=${encodeURIComponent(data.combinedLabelUrl)}`);
                    const zplText = await proxyRes.text();

                    const labelMatches = zplText.match(/\^XA[\s\S]*?\^XZ/g) || [];

                    if (labelMatches.length > 0) {
                        const bulkRes = await fetch(`${ENDPOINT_PATH}/api/proxy-labelary-bulk`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ zpls: labelMatches })
                        });

                        if (bulkRes.ok) {
                            const bulkData = await bulkRes.json();
                            const images = (bulkData.images || [])
                                .filter(Boolean)
                                .map(base64 => {
                                    const byteChars = atob(base64);
                                    const byteArr = new Uint8Array(byteChars.length);
                                    for (let i = 0; i < byteChars.length; i++) {
                                        byteArr[i] = byteChars.charCodeAt(i);
                                    }
                                    const blob = new Blob([byteArr], { type: 'image/png' });
                                    return URL.createObjectURL(blob);
                                });
                            setLabelImages(images);
                        }
                    }
                } catch (e) {
                    console.error('Error rendering label previews:', e);
                }
            }
        } catch (error) {
            console.error('Error loading labels:', error);
        }
    };
    
    const loadFinalizedProducts = async () => {
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/load-finalized-products?orderID=${currentOrderID}`);
            const data = await response.json();
            setProducts(data.products || []);
        } catch (error) {
            console.error('Error loading finalized products:', error);
        }
    };

    useEffect(() => {
        if (currentPage === 'view' && currentOrderID) {
            loadLabels();
            loadFinalizedProducts();
            setPageSelectMode('all');
            setPageRangeInput('');
            setPageRangeError('');
        }
    }, [currentPage, currentOrderID]);

    const handleDownloadLabel = async (url) => {
    try {
        const proxyUrl = `${ENDPOINT_PATH}/api/proxy-label-file?url=${encodeURIComponent(url)}`;
        const response = await fetch(proxyUrl);
        const zplText = await response.text();

        const labelMatches = (zplText.match(/\^XA[\s\S]*?\^XZ/g) || []).reverse();
        if (labelMatches.length === 0) {
            alert('No labels found in file.');
            return;
        }

        // Shared rendering constants — must match handlePrintLabel exactly
        const LABEL_W_IN  = 2.3125;
        const LABEL_H_IN  = 4;
        const DPI         = 300;
        const outW        = Math.round(LABEL_W_IN * DPI); // 694px
        const outH        = Math.round(LABEL_H_IN * DPI); // 1200px
        const marginLeft  = 0;
        const marginRight = 20;
        const marginTop   = 0;
        const marginBot   = 0;
        const offsetX     = 10;
        const offsetY     = 10;

        const renderLabel = (blob) => new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(blob);
            img.onload = () => {
                const offscreen = document.createElement('canvas');
                offscreen.width  = img.width;
                offscreen.height = img.height;
                const offCtx = offscreen.getContext('2d');
                offCtx.drawImage(img, 0, 0);
                const pixels = offCtx.getImageData(0, 0, img.width, img.height).data;

                const isWhite = (x, y) => {
                    const i = (y * img.width + x) * 4;
                    return pixels[i] > 230 && pixels[i+1] > 230 && pixels[i+2] > 230;
                };

                let top = 0;
                topLoop: for (let y = 0; y < img.height; y++) {
                    for (let x = 0; x < img.width; x++) {
                        if (!isWhite(x, y)) { top = y; break topLoop; }
                    }
                }
                let bottom = img.height - 1;
                bottomLoop: for (let y = img.height - 1; y >= 0; y--) {
                    for (let x = 0; x < img.width; x++) {
                        if (!isWhite(x, y)) { bottom = y; break bottomLoop; }
                    }
                }
                let left = 0;
                leftLoop: for (let x = 0; x < img.width; x++) {
                    for (let y = 0; y < img.height; y++) {
                        if (!isWhite(x, y)) { left = x; break leftLoop; }
                    }
                }
                let right = img.width - 1;
                rightLoop: for (let x = img.width - 1; x >= 0; x--) {
                    for (let y = 0; y < img.height; y++) {
                        if (!isWhite(x, y)) { right = x; break rightLoop; }
                    }
                }

                const pad  = 10;
                const srcX = Math.max(0, left - pad);
                const srcY = Math.max(0, top  - pad);
                const srcW = Math.min(img.width,  right  + pad) - srcX;
                const srcH = Math.min(img.height, bottom + pad) - srcY;

                const canvas = document.createElement('canvas');
                canvas.width  = outW;
                canvas.height = outH;
                const ctx = canvas.getContext('2d');

                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, outW, outH);

                const contentW = outW - marginLeft - marginRight;
                const contentH = outH - marginTop  - marginBot;
                const destX    = (-outW / 2) + marginLeft + offsetX;
                const destY    = (-outH / 2) + marginTop  + offsetY;

                ctx.translate(outW / 2, outH / 2);
                ctx.rotate(Math.PI);
                ctx.drawImage(img, srcX, srcY, srcW, srcH, destX, destY, contentW, contentH);

                URL.revokeObjectURL(objectUrl);
                resolve(canvas.toDataURL('image/png').split(',')[1]);
            };
            img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
            img.src = objectUrl;
        });

        const bulkRes = await fetch(`${ENDPOINT_PATH}/api/proxy-labelary-bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ zpls: labelMatches })
        });
        if (!bulkRes.ok) throw new Error('Label render failed');
        const bulkData = await bulkRes.json();
        const renderedBase64s = await Promise.all(
            (bulkData.images || []).map(async (base64) => {
                const byteChars = atob(base64);
                const byteArr = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                const blob = new Blob([byteArr], { type: 'image/png' });
                return await renderLabel(blob);
            })
        );

        const selectedIndices = pageSelectMode === 'range'
            ? parsePageRange(pageRangeInput, renderedBase64s.length)
            : Array.from({ length: renderedBase64s.length }, (_, i) => i);

        if (selectedIndices.length === 0) {
            alert('No valid box labels selected. Please check your box label range.');
            return;
        }

        const filteredBase64s = selectedIndices.map(i => renderedBase64s[i]);
        const imagesArrayLiteral = '[' + filteredBase64s.map(b => `"${b}"`).join(',\n') + ']';

        const pywContent = `#!/usr/bin/env python3
import sys
import subprocess
import importlib
import platform

IS_WINDOWS = platform.system() == "Windows"
IS_MAC     = platform.system() == "Darwin"

if IS_WINDOWS:
    for pkg, imp in [("pywin32", "win32com"), ("Pillow", "PIL")]:
        try:
            importlib.import_module(imp)
        except ImportError:
            subprocess.check_call(
                [sys.executable, "-m", "pip", "install", "--user", pkg],
                stdout=subprocess.DEVNULL,
                stderr=subprocess.DEVNULL,
            )
else:
    try:
        importlib.import_module("PIL")
    except ImportError:
        subprocess.check_call(
            [sys.executable, "-m", "pip", "install", "--user", "Pillow"],
            stdout=subprocess.DEVNULL,
            stderr=subprocess.DEVNULL,
        )

import threading, time, base64, io, tempfile, os
import tkinter as tk
from tkinter import scrolledtext, ttk
from PIL import Image

if IS_WINDOWS:
    import win32print
    import win32ui
    import win32con
    from PIL import ImageWin

LABEL_IMAGES_B64 = ${imagesArrayLiteral}

LABEL_WIDTH_IN  = 2.3125
LABEL_HEIGHT_IN = 4.0


def list_all_printers():
    if IS_WINDOWS:
        return [p[2] for p in win32print.EnumPrinters(
            win32print.PRINTER_ENUM_LOCAL | win32print.PRINTER_ENUM_CONNECTIONS)]
    else:
        try:
            result = subprocess.run(["lpstat", "-p"], capture_output=True, text=True)
            printers = []
            for line in result.stdout.splitlines():
                if line.startswith("printer "):
                    parts = line.split()
                    if len(parts) >= 2:
                        printers.append(parts[1])
            return printers
        except Exception:
            return []


def find_dymo_default(printers):
    for p in printers:
        if "dymo" in p.lower():
            return p
    return printers[0] if printers else ""


def print_via_gdi(printer_name, img_b64, log_fn):
    img_bytes = base64.b64decode(img_b64)
    img = Image.open(io.BytesIO(img_bytes)).convert("RGB")
    log_fn("  Printing via GDI to: " + printer_name, "dim")
    hprinter = win32print.OpenPrinter(printer_name)
    try:
        devmode = win32print.GetPrinter(hprinter, 8)["pDevMode"]
        devmode.PaperSize   = 0
        devmode.Fields      |= win32con.DM_PAPERSIZE | win32con.DM_PAPERLENGTH | win32con.DM_PAPERWIDTH
        devmode.PaperWidth  = int(2.3125 * 254)
        devmode.PaperLength = int(4.0    * 254)
        devmode.Orientation = win32con.DMORIENT_PORTRAIT
    finally:
        win32print.ClosePrinter(hprinter)
    hdc = win32ui.CreateDC()
    hdc.CreatePrinterDC(printer_name)
    pw = hdc.GetDeviceCaps(win32con.HORZRES)
    ph = hdc.GetDeviceCaps(win32con.VERTRES)
    log_fn("  Print canvas: " + str(pw) + "x" + str(ph) + " px", "dim")
    img_fit = img.resize((pw, ph), Image.LANCZOS)
    dib = ImageWin.Dib(img_fit)
    try:
        hdc.StartDoc("MCF Label")
        hdc.StartPage()
        dib.draw(hdc.GetHandleOutput(), (0, 0, pw, ph))
        hdc.EndPage()
        hdc.EndDoc()
        log_fn("  GDI print complete.", "ok")
    finally:
        hdc.DeleteDC()


def print_via_lp(printer_name, img_b64_list, log_fn):
    tmp_paths = []
    for img_b64 in img_b64_list:
        img_bytes = base64.b64decode(img_b64)
        img = Image.open(io.BytesIO(img_bytes)).convert("RGB")
        MAC_CROP_TOP    = 0
        MAC_CROP_BOTTOM = 0
        MAC_CROP_LEFT   = 0
        MAC_CROP_RIGHT  = 0
        MAC_OFFSET_X    = 4
        MAC_OFFSET_Y    = 38
        MAC_SCALE_X     = 1.00
        MAC_SCALE_Y     = 0.98
        w, h = img.size
        img = img.crop((
            MAC_CROP_LEFT,
            MAC_CROP_TOP,
            w - MAC_CROP_RIGHT if MAC_CROP_RIGHT > 0 else w,
            h - MAC_CROP_BOTTOM if MAC_CROP_BOTTOM > 0 else h
        ))
        cropped_w, cropped_h = img.size
        scaled_w = int(cropped_w * MAC_SCALE_X)
        scaled_h = int(cropped_h * MAC_SCALE_Y)
        img = img.resize((scaled_w, scaled_h), Image.LANCZOS)
        new_img = Image.new("RGB", (w, h), (255, 255, 255))
        paste_x = MAC_CROP_LEFT + MAC_OFFSET_X
        paste_y = MAC_CROP_TOP  + MAC_OFFSET_Y
        new_img.paste(img, (paste_x, paste_y))
        img = new_img
        with tempfile.NamedTemporaryFile(suffix=".png", delete=False) as tmp:
            tmp_path = tmp.name
            img.save(tmp_path, format="PNG", dpi=(300, 300))
        tmp_paths.append(tmp_path)
    log_fn("  Printing via lp to: " + printer_name, "dim")
    try:
        result = subprocess.run(
            [
                "lp",
                "-d", printer_name,
                "-o", "media=Custom.2.3125x4in",
                "-o", "fit-to-page",
                "-o", "orientation-requested=3",
                "-o", "page-top=0",
            ] + tmp_paths,
            capture_output=True, text=True
        )
        if result.returncode != 0:
            raise RuntimeError(result.stderr.strip() or "lp failed")
        log_fn("  lp print submitted.", "ok")
    finally:
        for p in tmp_paths:
            os.unlink(p)


class App(tk.Tk):
    def __init__(self):
        super().__init__()
        self.title("Label Printer – Order ${currentOrderID}")
        self.geometry("640x560")
        self.resizable(True, True)

        if IS_MAC:
            style = ttk.Style(self)
            style.theme_use("aqua")

        if IS_WINDOWS:
            self.configure(bg="#f5f5f5")
            BODY_BG  = "#f5f5f5"
            LABEL_FG = "#333333"
            HINT_FG  = "#666666"
        else:
            BODY_BG  = None
            LABEL_FG = None
            HINT_FG  = None

        # Header
        hdr = tk.Frame(self, bg="#ff4172", pady=10)
        hdr.pack(fill="x")
        tk.Label(hdr, text="MyCustomFrame  |  Label Printer",
                 fg="white", bg="#ff4172",
                 font=("Helvetica", 14, "bold")).pack()
        tk.Label(hdr, text="Order ${currentOrderID}  –  ${renderedBase64s.length} label(s) ready to print",
                 fg="#ffcdd8", bg="#ff4172",
                 font=("Helvetica", 10)).pack()

        # Printer selector
        sel_kwargs = {"padx": 12, "pady": 8}
        if BODY_BG: sel_kwargs["bg"] = BODY_BG
        sel_frame = tk.Frame(self, **sel_kwargs)
        sel_frame.pack(fill="x")

        lbl_kw = {"font": ("Helvetica", 10, "bold")}
        if BODY_BG:  lbl_kw["bg"] = BODY_BG
        if LABEL_FG: lbl_kw["fg"] = LABEL_FG
        tk.Label(sel_frame, text="Printer:", **lbl_kw).pack(side="left")

        self.printer_var = tk.StringVar()
        self.printer_combo = ttk.Combobox(
            sel_frame, textvariable=self.printer_var,
            state="readonly", font=("Helvetica", 10), width=38)
        self.printer_combo.pack(side="left", padx=(8, 8))

        ttk.Button(sel_frame, text="↻ Refresh",
                   command=self.refresh_printers).pack(side="left")

        self.refresh_printers()

        # Log area
        lf_kw = {"padx": 12, "pady": 4}
        if BODY_BG: lf_kw["bg"] = BODY_BG
        lf = tk.Frame(self, **lf_kw)
        lf.pack(fill="both", expand=True)

        log_lbl_kw = {"font": ("Helvetica", 10, "bold"), "anchor": "w"}
        if BODY_BG:  log_lbl_kw["bg"] = BODY_BG
        if LABEL_FG: log_lbl_kw["fg"] = LABEL_FG
        tk.Label(lf, text="Log:", **log_lbl_kw).pack(fill="x")

        self.txt = scrolledtext.ScrolledText(
            lf, state="disabled", font=("Courier New", 9),
            bg="#1e1e1e", fg="#d4d4d4", relief="flat", bd=0, height=20)
        self.txt.pack(fill="both", expand=True, pady=(4, 0))
        for tag, color in [("ok","#4ec9b0"),("err","#f44747"),("step","#c586c0"),
                            ("info","#569cd6"),("warn","#dcdcaa"),("dim","#9cdcfe")]:
            self.txt.tag_config(tag, foreground=color)

        # Bottom bar
        bot_kw = {"padx": 12, "pady": 8}
        if BODY_BG: bot_kw["bg"] = BODY_BG
        bot = tk.Frame(self, **bot_kw)
        bot.pack(fill="x")

        status_lbl_kw = {"font": ("Helvetica", 9), "anchor": "w"}
        if BODY_BG: status_lbl_kw["bg"] = BODY_BG
        if HINT_FG: status_lbl_kw["fg"] = HINT_FG
        self.status_var = tk.StringVar(value="Ready.")
        tk.Label(bot, textvariable=self.status_var, **status_lbl_kw).pack(
            side="left", expand=True, fill="x")

        if IS_WINDOWS:
            style = ttk.Style(self)
            style.theme_use("clam")
            style.configure("Pink.TButton",
                             background="#ff4172", foreground="white",
                             font=("Helvetica", 10, "bold"), relief="flat", padding=6)
            style.map("Pink.TButton",
                      background=[("active", "#e63361"), ("pressed", "#c82b54")])
            style.configure("Grey.TButton",
                             background="#6c757d", foreground="white",
                             font=("Helvetica", 10), relief="flat", padding=6)
            style.map("Grey.TButton",
                      background=[("active", "#5a6268"), ("pressed", "#495057")])
            self.print_btn = ttk.Button(bot, text="Print Labels",
                                        style="Pink.TButton", command=self.start_print)
            self.print_btn.pack(side="right", padx=(8, 0))
            ttk.Button(bot, text="Close",
                       style="Grey.TButton", command=self.destroy).pack(side="right")
        else:
            self.print_btn = ttk.Button(bot, text="Print Labels", command=self.start_print)
            self.print_btn.pack(side="right", padx=(8, 0))
            ttk.Button(bot, text="Close", command=self.destroy).pack(side="right")

        self._worker = None

    def refresh_printers(self):
        printers = list_all_printers()
        if not printers:
            self.printer_combo["values"] = ["(no printers found)"]
            self.printer_var.set("(no printers found)")
            return
        self.printer_combo["values"] = printers
        current = self.printer_var.get()
        if current in printers:
            pass  # keep current selection
        else:
            default = find_dymo_default(printers)
            self.printer_var.set(default)

    def log(self, msg, tag="dim"):
        def _do():
            self.txt.configure(state="normal")
            self.txt.insert("end", "[" + time.strftime("%H:%M:%S") + "]  ", "dim")
            self.txt.insert("end", msg + "\\n", tag)
            self.txt.configure(state="disabled")
            self.txt.see("end")
        self.after_idle(_do)

    def set_status(self, msg):
        self.after_idle(lambda: self.status_var.set(msg))

    def start_print(self):
        if self._worker and self._worker.is_alive():
            return
        printer = self.printer_var.get()
        if not printer or printer == "(no printers found)":
            self.log("No printer selected.", "err")
            return
        self.print_btn.configure(state="disabled")
        self._worker = threading.Thread(target=self._run, args=(printer,), daemon=True)
        self._worker.start()

    def _run(self, printer):
        try:
            run_job(self, printer)
        except Exception as exc:
            self.log("FATAL: " + str(exc), "err")
            self.set_status("Failed – see log.")
        finally:
            self.after_idle(lambda: self.print_btn.configure(state="normal"))


def run_job(app, printer_name):
    app.log(str(len(LABEL_IMAGES_B64)) + " pre-rendered label(s) loaded.", "info")
    app.log("Label size: 2.3125\\" x 4.0\\" @ 300 DPI", "info")
    app.log("Platform: " + platform.system(), "info")
    app.log("Printer: " + printer_name, "info")

    if IS_WINDOWS:
        for i, b64 in enumerate(LABEL_IMAGES_B64, 1):
            app.log("Printing label " + str(i) + " of " + str(len(LABEL_IMAGES_B64)) + " ...", "step")
            app.set_status("Printing " + str(i) + "/" + str(len(LABEL_IMAGES_B64)) + " ...")
            print_via_gdi(printer_name, b64, app.log)
            app.log("Label " + str(i) + " sent to printer.", "ok")
            if i < len(LABEL_IMAGES_B64):
                time.sleep(1.5)
    else:
        app.log("Printing " + str(len(LABEL_IMAGES_B64)) + " label(s) as single lp job...", "step")
        app.set_status("Printing " + str(len(LABEL_IMAGES_B64)) + " label(s)...")
        print_via_lp(printer_name, LABEL_IMAGES_B64, app.log)
        app.log("All labels sent to printer.", "ok")

    app.log("All " + str(len(LABEL_IMAGES_B64)) + " label(s) printed!", "ok")
    app.set_status("Done – " + str(len(LABEL_IMAGES_B64)) + " label(s) printed.")


if __name__ == "__main__":
    app = App()
    os_note = "Windows GDI" if IS_WINDOWS else ("macOS CUPS (lp)" if IS_MAC else "CUPS (lp)")
    app.log("Ready. Select a printer and click Print Labels.", "info")
    app.log("Prints directly via " + os_note + " — no DYMO software required.", "info")
    app.mainloop()
`;

        const blob = new Blob([pywContent], { type: 'text/plain' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Order_${currentOrderID}_Labels.pyw`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

    } catch (error) {
        console.error('Error generating label file:', error);
        alert('Failed to generate label file');
    }
};

    const loadQZTray = () => {
        return new Promise((resolve, reject) => {
            if (window.qz) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/qz-tray@2.2.4/qz-tray.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load QZ Tray script'));
            document.head.appendChild(script);
        });
    };

    const handleDownloadLaserLabels = async () => {
        try {
            if (labelImages.length === 0) {
                alert('No label previews loaded yet. Please wait for labels to finish loading.');
                return;
            }

            // Convert each label image URL (blob URL or data URL) to base64
            const toBase64 = async (src) => {
                const response = await fetch(src);
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result); // full data URL
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            };

            const allBase64Images = await Promise.all(labelImages.map(toBase64));

            const selectedIndices = pageSelectMode === 'range'
                ? parsePageRange(pageRangeInput, allBase64Images.length)
                : Array.from({ length: allBase64Images.length }, (_, i) => i);

            if (selectedIndices.length === 0) {
                alert('No valid box labels selected. Please check your box label range.');
                return;
            }

            const base64Images = selectedIndices.map(i => allBase64Images[i]);

            // Build a fully self-contained offline HTML file with embedded images
            const labelPages = base64Images.map((src, idx) => `
                <div class="label-page">
                    <img src="${src}" alt="Label ${idx + 1}" />
                </div>
            `).join('');

            const htmlContent = `<!DOCTYPE html>
    <html>
    <head>
        <title>Laser Labels - Order ${currentOrderID}</title>
        <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            html, body { background: white; }

            /* Screen view: centered labels with a print button */
            body {
                font-family: Arial, sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
                gap: 20px;
            }

            .toolbar {
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 16px 24px;
                background: #fff;
                border: 2px solid #ff4172;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.12);
                width: 100%;
                max-width: 600px;
            }

            .toolbar h2 {
                color: #ff4172;
                font-size: 16px;
                flex: 1;
            }

            .toolbar p {
                color: #666;
                font-size: 13px;
            }

            .print-btn {
                padding: 10px 22px;
                background: #ff4172;
                color: white;
                border: none;
                border-radius: 5px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                flex-shrink: 0;
            }

            .print-btn:hover { background: #e63361; }

            .label-page {
                display: flex;
                justify-content: center;
                width: 100%;
                max-width: 600px;
                border: 1px solid #dee2e6;
                border-radius: 6px;
                overflow: hidden;
                background: white;
            }

            .label-page img {
                display: block;
                width: 100%;
                height: auto;
            }

            /* Print styles: one label per page, no toolbar, full size */
            @media print {
                body {
                    display: block;
                    padding: 0;
                    gap: 0;
                }

                .toolbar { display: none; }

                .label-page {
                    display: block;
                    width: 100vw;
                    height: 100vh;
                    max-width: none;
                    border: none;
                    border-radius: 0;
                    page-break-before: always;
                    page-break-after: always;
                    page-break-inside: avoid;
                    overflow: hidden;
                }

                .label-page:first-of-type {
                    page-break-before: avoid;
                }

                .label-page img {
                    display: block;
                    width: auto;
                    height: 100vh;
                    max-width: 100%;
                    margin: 0 auto;
                }
            }
        </style>
    </head>
    <body>
        <div class="toolbar">
            <div>
                <h2>MyCustomFrame — Laser Labels</h2>
                <p>Order ${currentOrderID} &nbsp;·&nbsp; ${base64Images.length} label(s)</p>
            </div>
            <button class="print-btn" onclick="window.print()">🖨️ Print Labels</button>
        </div>

        ${labelPages}
    </body>
    </html>`;

            const blob = new Blob([htmlContent], { type: 'text/html' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `Order_${currentOrderID}_Laser_Labels.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.error('Error generating laser label file:', error);
            alert('Failed to generate laser label file');
        }
    };

    const handleLaserPrint = async () => {
        try {
            if (labelImages.length === 0) {
                alert('No label previews loaded yet. Please wait for labels to finish loading.');
                return;
            }

            // Build a printable HTML page from the label images
            const selectedIndices = pageSelectMode === 'range'
                ? parsePageRange(pageRangeInput, labelImages.length)
                : Array.from({ length: labelImages.length }, (_, i) => i);

            if (selectedIndices.length === 0) {
                alert('No valid box labels selected. Please check your box label range.');
                return;
            }

            const filteredLabelImages = selectedIndices.map(i => labelImages[i]);

            const labelImgTags = filteredLabelImages.map((imgSrc, idx) => `
                <div class="label-page">
                    <img src="${imgSrc}" alt="Label ${idx + 1}" />
                </div>
            `).join('');

            const printHTML = `<!DOCTYPE html>
    <html>
    <head>
        <title>Labels - Order ${currentOrderID}</title>
        <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            html, body { height: 100%; background: white; }
            .label-page {
                display: block;
                width: 100vw;
                height: 100vh;
                page-break-before: always;
                page-break-after: always;
                page-break-inside: avoid;
                overflow: hidden;
            }
            .label-page:first-child {
                page-break-before: avoid;
            }
            .label-page img {
                display: block;
                width: auto;
                height: 100vh;
                max-width: 100%;
                margin: 0 auto;
            }
            @media print {
                * { margin: 0; padding: 0; }
                .label-page {
                    display: block;
                    width: 100vw;
                    height: 100vh;
                    page-break-before: always;
                    page-break-after: always;
                    page-break-inside: avoid;
                    overflow: hidden;
                }
                .label-page:first-child {
                    page-break-before: avoid;
                }
                .label-page img {
                    display: block;
                    width: auto;
                    height: 100vh;
                    max-width: 100%;
                    margin: 0 auto;
                }
            }
        </style>
    </head>
    <body>
        ${labelImgTags}
    </body>
    </html>`;

            const blob = new Blob([printHTML], { type: 'text/html' });
            const blobUrl = window.URL.createObjectURL(blob);

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                setTimeout(() => {
                    try {
                        iframe.contentWindow.print();
                    } catch (error) {
                        console.error('Print error:', error);
                    }
                }, 500);
            };

            const cleanup = () => {
                window.removeEventListener('focus', cleanup);
                setTimeout(() => {
                    if (iframe.parentNode) {
                        document.body.removeChild(iframe);
                    }
                    window.URL.revokeObjectURL(blobUrl);
                }, 100);
            };

            window.addEventListener('focus', cleanup);

        } catch (error) {
            console.error('Error laser printing labels:', error);
            alert('Failed to print labels');
        }
    };

    const handlePrintLabel = async (url) => {
        try {
            const proxyUrl = `${ENDPOINT_PATH}/api/proxy-label-file?url=${encodeURIComponent(url)}`;
            const response = await fetch(proxyUrl);
            const zplText = await response.text();

            const labelMatches = (zplText.match(/\^XA[\s\S]*?\^XZ/g) || []).reverse();
            if (labelMatches.length === 0) {
                alert('No labels found in the ZPL file.');
                return;
            }

            const outW = Math.round(2.3125 * 300);
            const outH = Math.round(4 * 300);
            const marginLeft = 0, marginRight = 20, marginTop = 0, marginBot = 0;
            const offsetX = 10, offsetY = 10;

            const renderLabel = (blob) => new Promise((resolve, reject) => {
                const img = new Image();
                const objectUrl = URL.createObjectURL(blob);
                img.onload = () => {
                    const offscreen = document.createElement('canvas');
                    offscreen.width = img.width; offscreen.height = img.height;
                    const offCtx = offscreen.getContext('2d');
                    offCtx.drawImage(img, 0, 0);
                    const pixels = offCtx.getImageData(0, 0, img.width, img.height).data;
                    const isWhite = (x, y) => { const i = (y * img.width + x) * 4; return pixels[i] > 230 && pixels[i+1] > 230 && pixels[i+2] > 230; };
                    let top = 0, bottom = img.height - 1, left = 0, right = img.width - 1;
                    topLoop: for (let y = 0; y < img.height; y++) for (let x = 0; x < img.width; x++) if (!isWhite(x, y)) { top = y; break topLoop; }
                    bottomLoop: for (let y = img.height - 1; y >= 0; y--) for (let x = 0; x < img.width; x++) if (!isWhite(x, y)) { bottom = y; break bottomLoop; }
                    leftLoop: for (let x = 0; x < img.width; x++) for (let y = 0; y < img.height; y++) if (!isWhite(x, y)) { left = x; break leftLoop; }
                    rightLoop: for (let x = img.width - 1; x >= 0; x--) for (let y = 0; y < img.height; y++) if (!isWhite(x, y)) { right = x; break rightLoop; }
                    const pad = 10;
                    const srcX = Math.max(0, left - pad), srcY = Math.max(0, top - pad);
                    const srcW = Math.min(img.width, right + pad) - srcX, srcH = Math.min(img.height, bottom + pad) - srcY;
                    const canvas = document.createElement('canvas');
                    canvas.width = outW; canvas.height = outH;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, outW, outH);
                    const contentW = outW - marginLeft - marginRight, contentH = outH - marginTop - marginBot;
                    const destX = (-outW / 2) + marginLeft + offsetX, destY = (-outH / 2) + marginTop + offsetY;
                    ctx.translate(outW / 2, outH / 2); ctx.rotate(Math.PI);
                    ctx.drawImage(img, srcX, srcY, srcW, srcH, destX, destY, contentW, contentH);
                    URL.revokeObjectURL(objectUrl);
                    resolve(canvas.toDataURL('image/png').split(',')[1]);
                };
                img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load label image')); };
                img.src = objectUrl;
            });

            const bulkRes = await fetch(`${ENDPOINT_PATH}/api/proxy-labelary-bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ zpls: labelMatches })
            });
            if (!bulkRes.ok) throw new Error(`Labelary bulk conversion failed: ${bulkRes.status}`);
            const bulkData = await bulkRes.json();
            const allRendered = await Promise.all(
                (bulkData.images || []).map(async (base64) => {
                    const byteChars = atob(base64);
                    const byteArr = new Uint8Array(byteChars.length);
                    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
                    const blob = new Blob([byteArr], { type: 'image/png' });
                    return await renderLabel(blob);
                })
            );

            const selectedIndices = pageSelectMode === 'range'
                ? parsePageRange(pageRangeInput, allRendered.length)
                : Array.from({ length: allRendered.length }, (_, i) => i);

            if (selectedIndices.length === 0) {
                alert('No valid box labels selected. Please check your box label range.');
                return;
            }

            const printLabelImages = selectedIndices.map(i => allRendered[i]);

            const printResponse = await fetch('http://127.0.0.1:9100/print', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ images: printLabelImages })
            });

            const result = await printResponse.json();
            if (!result.ok) throw new Error(result.error || 'Print failed');
            alert(`✓ ${result.printed} label(s) sent to DYMO printer!`);

        } catch (error) {
            console.error('Error printing labels:', error);
            if (error.message.includes('fetch') || error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                setPrintServerPopup(true);
            } else {
                alert('Failed to print labels.\n\nError: ' + error.message);
            }
        }
    };

    const handleDownloadPrintServer = async () => {
        try {
            const response = await fetch(`${ENDPOINT_PATH}/api/download-print-server`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = 'mcf_print_server.zip';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading print server:', error);
        }
    };

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
    };

    // ============ RENDER ============
    
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    return (
        <div>
            <AdminNavbar />
            <hr className="admin-divider" />
            <div className="dimension-requests-page">
                <Sidebar userType={userType || 'admin'} />
                <div className="dimension-requests-content">
                    
                    {/* PAGE 1: LIST */}
                    {currentPage === 'list' && (
                        <>
                            <div className="header">
                                <div className="filters">
                                    <button 
                                        className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('all')}
                                    >
                                        All
                                    </button>
                                    <button 
                                        className={`filter-btn ${filterType === 'unfinished' ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('unfinished')}
                                    >
                                        Unfinished
                                    </button>
                                    <button 
                                        className={`filter-btn ${filterType === 'finished' ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('finished')}
                                    >
                                        Finished
                                    </button>
                                    <button 
                                        className={`filter-btn ${filterType === 'dateRange' ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('dateRange')}
                                    >
                                        Date Range
                                    </button>
                                    <button 
                                        className={`filter-btn ${filterType === 'search' ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('search')}
                                    >
                                        Search
                                    </button>
                                </div>

                                {filterType === 'search' && (
                                    <div className="search-controls">
                                        <select 
                                            value={searchItem} 
                                            onChange={(e) => handleSearchItemChange(e.target.value)}
                                            className="search-dropdown"
                                        >
                                            <option value="all-fields">All Fields</option>
                                            <option value="status">Status</option>
                                            <option value="orderID">Order ID</option>
                                            <option value="name">Name</option>
                                            <option value="email">Email</option>
                                            <option value="address">Address</option>
                                            <option value="productCount">Product Count</option>
                                            <option value="totalPrice">Total Price</option>
                                            <option value="purchaseDate">Purchase Date</option>
                                        </select>
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="Search..."
                                            value={searchInput}
                                            onChange={(e) => handleSearchInputChange(e.target.value)}
                                        />
                                    </div>
                                )}
                                
                                {filterType === 'dateRange' && (
                                    <div className="search-controls" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <div className="date-input-group">
                                                <label>Begin:</label>
                                                <input
                                                    type="date"
                                                    value={dateRangeBegin}
                                                    onChange={(e) => handleDateRangeChange(e.target.value, dateRangeEnd)}
                                                    className="date-range-input"
                                                />
                                            </div>
                                            <div className="date-input-group">
                                                <label>End:</label>
                                                <input
                                                    type="date"
                                                    value={dateRangeEnd}
                                                    onChange={(e) => handleDateRangeChange(dateRangeBegin, e.target.value)}
                                                    className="date-range-input"
                                                />
                                            </div>
                                        </div>
                                        {dateRangeError && (
                                            <span style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{dateRangeError}</span>
                                        )}
                                    </div>
                                )}
                                
                                <button className="refresh-btn" onClick={() => { fetchCount(); fetchRequests(); }}>Refresh</button>
                                <button className="clear-btn" onClick={handleClear}>Revert To Preset</button>
                                <button className="settings-gear-btn" onClick={() => {
                                    setSettingsSnapshot({
                                        filterMode: settingsFilterMode,
                                        sortMode: settingsSortMode,
                                        pageMode: settingsPageMode,
                                        filterValue: settingsFilterValue,
                                        filterSearchItem: settingsFilterSearchItem,
                                        filterSearchInput: settingsFilterSearchInput,
                                        filterDateBegin: settingsFilterDateBegin,
                                        filterDateEnd: settingsFilterDateEnd,
                                        sortValue: settingsSortValue,
                                        sortDirection: settingsSortDirection,
                                        pageValue: settingsPageValue,
                                    });
                                    setShowSettingsPopup(true);
                                    setTimeout(() => {
                                        if (settingsPageMode === 'selectedHere') fetchSettingsPageOptions();
                                    }, 50);
                                }} title="Settings">⚙️</button>
                            </div>

                            <div className="order-table-container" style={{ position: 'relative' }}>
                                {loading && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        background: 'rgba(255,255,255,0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 10,
                                        borderRadius: '8px'
                                    }}>
                                        <div className="loading" style={{ background: 'white', padding: '10px 20px', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>Loading...</div>
                                    </div>
                                )}
                                <table className="order-table">
                                        <thead>
                                            <tr>
                                                {[
                                                    { label: 'STATUS', field: 'status' },
                                                    { label: 'ORDER ID', field: 'orderID' },
                                                    { label: 'NAME', field: 'name' },
                                                    { label: 'EMAIL', field: 'email' },
                                                    { label: 'ADDRESS', field: 'address' },
                                                    { label: 'PRODUCTS', field: 'productCount' },
                                                    { label: 'TOTAL', field: 'totalPrice' },
                                                    { label: 'PURCHASE DATE', field: 'purchaseDate' },
                                                ].map(({ label, field }) => (
                                                    <th
                                                        key={field}
                                                        onClick={() => handleSortClick(field)}
                                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                        className={sortField === field ? 'sort-th--active' : ''}
                                                    >
                                                        <span className="sort-btn-inner">
                                                            {label}
                                                            {sortField === field ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : (
                                                                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', fontSize: '8px', lineHeight: 1, gap: 0, marginLeft: '6px' }} aria-hidden>
                                                                    <span style={{ marginBottom: '-2px', opacity: 0.55 }}>▲</span>
                                                                    <span style={{ opacity: 0.55 }}>▼</span>
                                                                </span>
                                                            )}
                                                        </span>
                                                    </th>
                                                ))}
                                                <th>INTERACT WITH</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requests.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="no-data">No dimension requests found</td>
                                                </tr>
                                            ) : (
                                                requests.map((request, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <span className={`status ${
                                                                request.status === 'Begin' || request.status === 'Continue' 
                                                                    ? 'status-unfinished' 
                                                                    : 'status-finished'
                                                            }`}>
                                                                {request.status}
                                                            </span>
                                                        </td>
                                                        <td>{request.orderID}</td>
                                                        <td>{request.name}</td>
                                                        <td>{request.email}</td>
                                                        <td>{request.fullAddress}</td>
                                                        <td>{request.productCount}</td>
                                                        <td>${request.totalPrice.toFixed(2)}</td>
                                                        <td>{request.purchaseDate}</td>
                                                        <td>
                                                            <button 
                                                                className={`interact-btn ${request.isDisabled ? 'disabled-btn' : ''}`}
                                                                onClick={() => handleInteract(request)}
                                                                disabled={request.isDisabled}
                                                            >
                                                                {request.button}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>

                                {(requests.length > 0 || totalCount > 0) && (
                                    <div className="pagination">
                                        <button
                                            className="page-btn"
                                            onClick={() => {
                                                const newPage = pageNum - 1;
                                                setPageNum(newPage);
                                                savePagePreference(newPage);
                                            }}
                                            disabled={pageNum === 1}
                                        >
                                            Prev
                                        </button>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            {`Showing ${(pageNum - 1) * itemsPerPage + 1} - ${Math.min(
                                                pageNum * itemsPerPage,
                                                totalCount
                                            )} of ${totalCount} — Page `}
                                            <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={pageInputValue ?? pageNum}
                                                    onChange={(e) => setPageInputValue(e.target.value)}
                                                    onBlur={() => setPageInputValue(null)}
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter') {
                                                            const entered = parseInt(pageInputValue);
                                                            if (isNaN(entered) || entered < 1) {
                                                                setPageInputError('Please enter a valid page number.');
                                                                return;
                                                            }
                                                            const count = await fetchCount();
                                                            const totalPgs = Math.ceil(count / itemsPerPage);
                                                            if (entered > totalPgs) {
                                                                setPageInputError(`Page ${entered} does not exist. Max page is ${totalPgs}.`);
                                                                return;
                                                            }
                                                            setPageInputError('');
                                                            setPageNum(entered);
                                                            savePagePreference(entered);
                                                            setPageInputValue(null);
                                                        }
                                                    }}
                                                    style={{
                                                        width: '52px',
                                                        textAlign: 'center',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '4px',
                                                        padding: '2px 6px',
                                                        fontSize: '14px',
                                                        MozAppearance: 'textfield',
                                                    }}
                                                />
                                                {pageInputError && (
                                                    <span style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px', whiteSpace: 'nowrap', position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
                                                        {pageInputError}
                                                    </span>
                                                )}
                                            </span>
                                            {` of ${totalPages}`}
                                        </span>
                                        <button
                                            className="page-btn"
                                            onClick={() => {
                                                const newPage = pageNum + 1;
                                                setPageNum(newPage);
                                                savePagePreference(newPage);
                                            }}
                                            disabled={pageNum >= totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Bulk Combine Labels Panel */}
                            <div className="bulk-combine-panel">
                                <h4>Bulk Combine Labels</h4>
                                <div className="bulk-combine-controls">
                                    <div className="date-type-selection">
                                        <label>
                                            <input
                                                type="radio"
                                                name="bulkDateType"
                                                value="purchaseDate"
                                                checked={bulkDateType === 'purchaseDate'}
                                                onChange={(e) => setBulkDateType(e.target.value)}
                                            />
                                            Purchase Date
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name="bulkDateType"
                                                value="labelGenerationDate"
                                                checked={bulkDateType === 'labelGenerationDate'}
                                                onChange={(e) => setBulkDateType(e.target.value)}
                                            />
                                            Label Generation Date
                                        </label>
                                    </div>
                                    <div className="date-range-selection">
                                        <div className="date-input-group">
                                            <label>Begin Date:</label>
                                            <input
                                                type="date"
                                                value={bulkBeginDate}
                                                onChange={(e) => setBulkBeginDate(e.target.value)}
                                                className="bulk-date-input"
                                            />
                                        </div>
                                        <div className="date-input-group">
                                            <label>End Date:</label>
                                            <input
                                                type="date"
                                                value={bulkEndDate}
                                                onChange={(e) => setBulkEndDate(e.target.value)}
                                                className="bulk-date-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="bulk-label-actions">
                                        <div className="bulk-label-actions-section">
                                            <p className="bulk-label-actions-header">Dymo LabelWriter</p>
                                            <button className="bulk-combine-btn" onClick={handleBulkThermalPrint} disabled={bulkCombining}>
                                                {bulkCombining ? 'Processing...' : 'Print'}
                                            </button>
                                            <button className="bulk-combine-btn" onClick={handleBulkDownloadThermalLabels} disabled={bulkCombining}>
                                                {bulkCombining ? 'Processing...' : 'Download'}
                                            </button>
                                            <span onClick={handleDownloadPrintServer} style={{ display: 'block', textAlign: 'center', fontSize: '12px', color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}>Print Server Download</span>
                                        </div>
                                        <div className="bulk-label-actions-section">
                                            <p className="bulk-label-actions-header">Regular Printers</p>
                                            <button
                                                className="bulk-combine-btn"
                                                onClick={handleBulkLaserPrint}
                                                disabled={bulkCombining}
                                            >
                                                {bulkCombining ? 'Processing...' : 'Print'}
                                            </button>
                                            <button
                                                className="bulk-combine-btn"
                                                onClick={handleBulkDownloadLaserLabels}
                                                disabled={bulkCombining}
                                            >
                                                {bulkCombining ? 'Processing...' : 'Download'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    

                    {/* PAGE 2: EDIT */}
                    {currentPage === 'edit' && (
                        <>
                            <div className="edit-header">
                                <div className="edit-header-left-section">
                                    <button 
                                        className="back-btn"
                                        onClick={() => {
                                            if (window.confirm('Return to dimension requests list? Any unsaved changes will be lost.')) {
                                                window.location.reload();
                                            }
                                        }}
                                    >
                                        ← Dimension Requests
                                    </button>
                                    {hasExistingLabels && (
                                        <button
                                            className="exit-btn"
                                            style={{ marginBottom: 0, width: 'auto', padding: '10px 20px', marginLeft: '10px' }}
                                            onClick={() => setCurrentPage('view')}
                                        >
                                            View Existing Labels →
                                        </button>
                                    )}
                                    <div className="order-details">
                                        <h2>Order #{orderInfo?.orderID}</h2>
                                        <p>Customer: {orderInfo?.name} ({orderInfo?.email})</p>
                                    </div>
                                </div>
                                
                                <div className="address-update-section">
                                    <div className="address-update-header">
                                        <h4>Shipping Address</h4>
                                        {!editingAddress ? (
                                            <button 
                                                className="edit-address-btn"
                                                onClick={() => setEditingAddress(true)}
                                            >
                                                Edit Address
                                            </button>
                                        ) : (
                                            <button 
                                                className="save-address-btn"
                                                onClick={handleSaveAddress}
                                                disabled={savingAddress}
                                            >
                                                {savingAddress ? 'Saving...' : 'Save Address'}
                                            </button>
                                        )}
                                    </div>
                                    
                                    {!editingAddress ? (
                                        <div className="address-display">
                                            <p><strong>Name:</strong> {addressFields.shippingFirstName} {addressFields.shippingLastName}</p>
                                            <p><strong>Address:</strong> {addressFields.address1}</p>
                                            {addressFields.address2 && <p><strong>Address 2:</strong> {addressFields.address2}</p>}
                                            <p><strong>City:</strong> {addressFields.city}</p>
                                            <p><strong>State:</strong> {addressFields.state}</p>
                                            <p><strong>Postal Code:</strong> {addressFields.postalCode}</p>
                                        </div>
                                    ) : (
                                        <div className="address-edit-fields">
                                            <div className="address-field-row">
                                                <input
                                                    type="text"
                                                    placeholder="First Name *"
                                                    value={addressFields.shippingFirstName}
                                                    onChange={(e) => handleAddressFieldChange('shippingFirstName', e.target.value)}
                                                    className="address-input"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Last Name *"
                                                    value={addressFields.shippingLastName}
                                                    onChange={(e) => handleAddressFieldChange('shippingLastName', e.target.value)}
                                                    className="address-input"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Address Line 1 *"
                                                value={addressFields.address1}
                                                onChange={(e) => handleAddressFieldChange('address1', e.target.value)}
                                                className="address-input full-width"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Address Line 2 (Optional)"
                                                value={addressFields.address2}
                                                onChange={(e) => handleAddressFieldChange('address2', e.target.value)}
                                                className="address-input full-width"
                                            />
                                            <div className="address-field-row">
                                                <input
                                                    type="text"
                                                    placeholder="City *"
                                                    value={addressFields.city}
                                                    onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                                                    className="address-input"
                                                />
                                                <select
                                                    value={addressFields.state}
                                                    onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                                                    className="address-input state-select"
                                                >
                                                    <option value="">Select State *</option>
                                                    <option value="AL">AL</option>
                                                    <option value="AK">AK</option>
                                                    <option value="AZ">AZ</option>
                                                    <option value="AR">AR</option>
                                                    <option value="CA">CA</option>
                                                    <option value="CO">CO</option>
                                                    <option value="CT">CT</option>
                                                    <option value="DE">DE</option>
                                                    <option value="FL">FL</option>
                                                    <option value="GA">GA</option>
                                                    <option value="HI">HI</option>
                                                    <option value="ID">ID</option>
                                                    <option value="IL">IL</option>
                                                    <option value="IN">IN</option>
                                                    <option value="IA">IA</option>
                                                    <option value="KS">KS</option>
                                                    <option value="KY">KY</option>
                                                    <option value="LA">LA</option>
                                                    <option value="ME">ME</option>
                                                    <option value="MD">MD</option>
                                                    <option value="MA">MA</option>
                                                    <option value="MI">MI</option>
                                                    <option value="MN">MN</option>
                                                    <option value="MS">MS</option>
                                                    <option value="MO">MO</option>
                                                    <option value="MT">MT</option>
                                                    <option value="NE">NE</option>
                                                    <option value="NV">NV</option>
                                                    <option value="NH">NH</option>
                                                    <option value="NJ">NJ</option>
                                                    <option value="NM">NM</option>
                                                    <option value="NY">NY</option>
                                                    <option value="NC">NC</option>
                                                    <option value="ND">ND</option>
                                                    <option value="OH">OH</option>
                                                    <option value="OK">OK</option>
                                                    <option value="OR">OR</option>
                                                    <option value="PA">PA</option>
                                                    <option value="RI">RI</option>
                                                    <option value="SC">SC</option>
                                                    <option value="SD">SD</option>
                                                    <option value="TN">TN</option>
                                                    <option value="TX">TX</option>
                                                    <option value="UT">UT</option>
                                                    <option value="VT">VT</option>
                                                    <option value="VA">VA</option>
                                                    <option value="WA">WA</option>
                                                    <option value="WV">WV</option>
                                                    <option value="WI">WI</option>
                                                    <option value="WY">WY</option>
                                                    <option value="DC">DC</option>
                                                </select>
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Postal Code *"
                                                value={addressFields.postalCode}
                                                onChange={(e) => handleAddressFieldChange('postalCode', e.target.value)}
                                                className="address-input full-width"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="edit-container">
                                <div className="left-pane">
                                    <h3>Products</h3>
                                    <div className="products-scroll-container">
                                        {products.length === 0 ? (
                                            <p>No products found</p>
                                        ) : (
                                            products.map(product => (
                                                <div key={product.productNumber} className="product-card">
                                                    <h4>Product {product.productNumber}</h4>
                                                    <div className="product-content">
                                                        <div className="product-image">
                                                            <FramePreview product={product} />
                                                        </div>
                                                        <div className="product-details">
                                                            <div className="box-selector">
                                                                <label>Box Selected:</label>
                                                                {editingProducts[product.productNumber] ? (
                                                                    <>
                                                                        <select
                                                                            value={selectedBoxes[product.productNumber] || 0}
                                                                            onChange={(e) => handleProductBoxChange(product.productNumber, e.target.value)}
                                                                        >
                                                                            <option value="0">None</option>
                                                                            {boxNumbers.length === 0 ? (
                                                                                <option disabled>No boxes available</option>
                                                                            ) : (
                                                                                boxNumbers.map(num => (
                                                                                    <option key={num} value={num}>Box {num}</option>
                                                                                ))
                                                                            )}
                                                                        </select>
                                                                        <button 
                                                                            className="save-btn"
                                                                            onClick={() => handleSaveProductBox(product.productNumber)}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span className="box-value">
                                                                            {product.boxSelected === 0 ? 'None' : `Box ${product.boxSelected}`}
                                                                        </span>
                                                                        <button 
                                                                            className="edit-btn"
                                                                            onClick={() => setEditingProducts(prev => ({
                                                                                ...prev,
                                                                                [product.productNumber]: true
                                                                            }))}
                                                                        >
                                                                            Edit
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="product-info">
                                                                <p><strong>Frame:</strong> {product.name || 'N/A'} ({product.frameThickness ? `${product.frameThickness}"` : 'N/A'} thick)</p>
                                                                <p><strong>Image Dimensions:</strong> {product.imageDimensions?.width || 'N/A'}" × {product.imageDimensions?.height || 'N/A'}"</p>
                                                                <p><strong>Orientation:</strong> {product.frameOrientation ? product.frameOrientation.charAt(0).toUpperCase() + product.frameOrientation.slice(1) : 'Portrait'}</p>
                                                                <p><strong>Glass:</strong> {product.glassType || 'None picked'}</p>
                                                                <p><strong>Hanging:</strong> {product.hangingStyle || 'None picked'}</p>
                                                                <p><strong>Medium:</strong> {product.medium || 'N/A'}</p>
                                                                <p><strong>Stretching Style:</strong> {product.stretchingStyle || 'None'}</p>
                                                                
                                                                {/* Matboard details - ALWAYS SHOW ALL THREE */}
                                                                <p><strong>Matboard 1:</strong> {
                                                                    product.matboardNum >= 1 && product.selectedMatboards && product.selectedMatboards[0] 
                                                                        ? `${product.selectedMatboards[0].displayName} (${product.selectedMatboards[0].style}, ${product.matboardThickness || 'N/A'}" thick)`
                                                                        : 'None picked'
                                                                }</p>
                                                                <p><strong>Matboard 2:</strong> {
                                                                    product.matboardNum >= 2 && product.selectedMatboards && product.selectedMatboards[1]
                                                                        ? `${product.selectedMatboards[1].displayName} (${product.selectedMatboards[1].style}, ${product.matboardThickness || 'N/A'}" thick)`
                                                                        : 'None picked'
                                                                }</p>
                                                                <p><strong>Matboard 3:</strong> {
                                                                    product.matboardNum >= 3 && product.selectedMatboards && product.selectedMatboards[2]
                                                                        ? `${product.selectedMatboards[2].displayName} (${product.selectedMatboards[2].style}, ${product.matboardThickness || 'N/A'}" thick)`
                                                                        : 'None picked'
                                                                }</p>
                                                                
                                                                <p><strong>SKU:</strong> {product.selectedFrame || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <div className="right-pane">
                                    <h3>Boxes</h3>
                                    
                                    {/* Auto Complete Section - always visible */}
                                    <div className="top-controls-row">
                                        <div className="auto-complete-section">
                                            <div className="auto-complete-controls">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    placeholder="Products per box"
                                                    value={productsPerBoxAuto}
                                                    onChange={(e) => setProductsPerBoxAuto(e.target.value)}
                                                    className="assign-products-input"
                                                />
                                                <button
                                                    className="auto-complete-btn"
                                                    onClick={handleAutoCompleteOrder}
                                                >
                                                    Auto Complete This Order
                                                </button>
                                            </div>
                                            {assignWarning && (
                                                <p className="assign-warning">{assignWarning}</p>
                                            )}
                                        </div>

                                        <div className="assign-products-section">
                                            <input
                                                type="number"
                                                min="1"
                                                placeholder="Products per box"
                                                value={productsPerBoxAssign}
                                                onChange={(e) => {
                                                    setProductsPerBoxAssign(e.target.value);
                                                    setAssignWarning('');
                                                }}
                                                className="assign-products-input"
                                            />
                                            <button
                                                className="assign-products-btn"
                                                onClick={handleAssignProductsToBoxes}
                                            >
                                                Assign Products to Boxes
                                            </button>
                                        </div>
                                    </div>

                                    <div className="box-bulk-actions">
                                        <div className="bulk-actions-left">
                                            <div className="bulk-action-checkboxes">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={boxes.length > 0 && boxes.every(box => selectedBoxCheckboxes[box.boxNumber])}
                                                        onChange={(e) => handleSelectAllBoxes(e.target.checked)}
                                                    />
                                                    Select All
                                                </label>
                                            </div>
                                            <div className="bulk-action-buttons">
                                                <button className="bulk-action-btn autofill-bulk-btn" onClick={handleBulkAutofill} title="Auto-Fill Selected">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2 19l3 3 17.36-17.36a1.21 1.21 0 0 0 0-1.72z"/><path d="m14 7 3 3"/><path d="M5 6v4"/><path d="M19 14v4"/><path d="M10 2v2"/><path d="M7 8H3"/><path d="M21 16h-4"/><path d="M11 3H9"/></svg>
                                                </button>
                                                <button className="bulk-action-btn edit-bulk-btn" onClick={handleBulkEditMode} title="Edit Selected">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                </button>
                                                <button className="bulk-action-btn save-bulk-btn" onClick={handleBulkSaveMode} title="Save Selected">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                                                </button>
                                                <button className="bulk-action-btn delete-bulk-btn" onClick={handleBulkDelete} title="Delete Selected">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bulk-actions-divider" />

                                        <div className="bulk-actions-right">
                                            <div className="bulk-copy-inputs">
                                                <input
                                                    type="text"
                                                    placeholder="L"
                                                    value={bulkCopyDims.length}
                                                    onChange={(e) => setBulkCopyDims(prev => ({ ...prev, length: e.target.value }))}
                                                    className="bulk-copy-input"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="W"
                                                    value={bulkCopyDims.width}
                                                    onChange={(e) => setBulkCopyDims(prev => ({ ...prev, width: e.target.value }))}
                                                    className="bulk-copy-input"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="H"
                                                    value={bulkCopyDims.height}
                                                    onChange={(e) => setBulkCopyDims(prev => ({ ...prev, height: e.target.value }))}
                                                    className="bulk-copy-input"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="W"
                                                    value={bulkCopyDims.weight}
                                                    onChange={(e) => setBulkCopyDims(prev => ({ ...prev, weight: e.target.value }))}
                                                    className="bulk-copy-input"
                                                />
                                                <button className="bulk-copy-btn" onClick={handleBulkCopy} title="Copy to Selected">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="boxes-scroll-container">
                                        {boxes.length === 0 ? (
                                            <p className="no-boxes">No boxes present in order, please add a box to get started.</p>
                                        ) : (
                                            boxes.map(box => (
                                                <div key={box.boxNumber} className="box-card">
                                                    <div className="box-header">
                                                        <div className="box-header-left">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedBoxCheckboxes[box.boxNumber] || false}
                                                                onChange={(e) => handleBoxCheckboxChange(box.boxNumber, e.target.checked)}
                                                                className="box-checkbox"
                                                            />
                                                            <h4>Box {box.boxNumber}</h4>
                                                        </div>
                                                        <div className="box-header-right">
                                                            <button
                                                                className="autofill-single-btn"
                                                                onClick={() => handleSingleBoxAutofill(box.boxNumber)}
                                                                title="Auto-Fill this box"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2 19l3 3 17.36-17.36a1.21 1.21 0 0 0 0-1.72z"/><path d="m14 7 3 3"/><path d="M5 6v4"/><path d="M19 14v4"/><path d="M10 2v2"/><path d="M7 8H3"/><path d="M21 16h-4"/><path d="M11 3H9"/></svg>
                                                            </button>
                                                            <button
                                                                className="copy-to-selected-btn"
                                                                onClick={() => handleCopyBoxToSelected(box.boxNumber)}
                                                                title="Copy this box to all selected boxes"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/><line x1="15" y1="3" x2="21" y2="3"/><line x1="18" y1="1" x2="18" y2="6"/></svg>
                                                            </button>
                                                            <button 
                                                                className="delete-btn"
                                                                onClick={() => handleDeleteBox(box.boxNumber)}
                                                                title="Delete box"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    
                                                    {editingBoxes[box.boxNumber] ? (
                                                        <>
                                                            <div className="preset-selector">
                                                                <select
                                                                    value={getSelectedPreset(box.boxNumber)}
                                                                    onChange={(e) => handlePresetChange(box.boxNumber, e.target.value)}
                                                                >
                                                                    <option value="select">Select dimension size</option>
                                                                    {Object.keys(presetDimensions).map(preset => (
                                                                        <option key={preset} value={preset}>{preset}</option>
                                                                    ))}
                                                                    <option value="custom">Custom</option>
                                                                </select>
                                                            </div>
                                                            <div className="box-dimensions">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Length"
                                                                    value={boxDimensions[box.boxNumber]?.length || ''}
                                                                    onChange={(e) => handleBoxDimensionChange(box.boxNumber, 'length', e.target.value)}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Width"
                                                                    value={boxDimensions[box.boxNumber]?.width || ''}
                                                                    onChange={(e) => handleBoxDimensionChange(box.boxNumber, 'width', e.target.value)}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Height"
                                                                    value={boxDimensions[box.boxNumber]?.height || ''}
                                                                    onChange={(e) => handleBoxDimensionChange(box.boxNumber, 'height', e.target.value)}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Weight"
                                                                    value={boxDimensions[box.boxNumber]?.weight || ''}
                                                                    onChange={(e) => handleBoxDimensionChange(box.boxNumber, 'weight', e.target.value)}
                                                                />
                                                                <button 
                                                                    className="save-btn"
                                                                    onClick={() => handleSaveBox(box.boxNumber)}
                                                                >
                                                                    Save Box {box.boxNumber}
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="box-dimensions-display">
                                                            <p><strong>Length:</strong> {box.length || 'Empty'}</p>
                                                            <p><strong>Width:</strong> {box.width || 'Empty'}</p>
                                                            <p><strong>Height:</strong> {box.height || 'Empty'}</p>
                                                            <p><strong>Weight:</strong> {box.weight || 'Empty'}</p>
                                                            <button 
                                                                className="edit-btn"
                                                                onClick={() => setEditingBoxes(prev => ({
                                                                    ...prev,
                                                                    [box.boxNumber]: true
                                                                }))}
                                                            >
                                                                Edit Box {box.boxNumber}
                                                            </button>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="box-products">
                                                        <p><strong>Product numbers in box:</strong> {box.productsAssociated}</p>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="box-actions">
                                        <button className="add-box-btn" onClick={handleAddBox}>
                                            + Add Box
                                        </button>
                                        <button 
                                            className="generate-labels-btn" 
                                            onClick={handleGenerateLabels}
                                            disabled={loading}
                                        >
                                            {loading ? 'Generating...' : 'Generate Labels →'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* PAGE 3: VIEW LABELS */}
                    {currentPage === 'view' && (
                        <>
                            <div className="edit-header">
                                <div className="edit-header-left-section">
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                                        <button
                                            className={`back-btn ${isShipped ? 'disabled-btn' : ''}`}
                                            onClick={() => !isShipped && setCurrentPage('edit')}
                                            disabled={isShipped}
                                        >
                                            {isShipped ? '🔒 Shipped' : '← Edit Boxes'}
                                        </button>
                                        <button
                                            className="exit-btn"
                                            style={{ marginBottom: 0, width: 'auto', padding: '10px 20px' }}
                                            onClick={() => window.location.reload()}
                                        >
                                            Exit back to dimension requests list
                                        </button>
                                    </div>
                                    <div className="order-details">
                                        <h2>Order #{orderInfo?.orderID}</h2>
                                        <p>Customer: {orderInfo?.name} ({orderInfo?.email})</p>
                                    </div>
                                </div>

                                <div className="address-update-section">
                                    <div className="address-update-header">
                                        <h4>Shipping Address</h4>
                                    </div>
                                    <div className="address-display">
                                        <p><strong>Name:</strong> {addressFields.shippingFirstName} {addressFields.shippingLastName}</p>
                                        <p><strong>Address:</strong> {addressFields.address1}</p>
                                        {addressFields.address2 && <p><strong>Address 2:</strong> {addressFields.address2}</p>}
                                        <p><strong>City:</strong> {addressFields.city}</p>
                                        <p><strong>State:</strong> {addressFields.state}</p>
                                        <p><strong>Postal Code:</strong> {addressFields.postalCode}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="view-container-three-pane">
                                {/* LEFT PANE: Products */}
                                <div className="view-pane">
                                    <h3>Products</h3>
                                    <div className="products-scroll-container">
                                        {products.length === 0 ? (
                                            <p>No products found</p>
                                        ) : (
                                            products.map(product => (
                                                <div key={product.productNumber} className="product-card">
                                                    <h4>Product {product.productNumber}</h4>
                                                    <div className="product-content">
                                                        <div className="product-image">
                                                            <FramePreview product={product} />
                                                        </div>
                                                        <div className="product-details">
                                                            <div className="box-info">
                                                                <p><strong>Box Selected:</strong> {product.boxSelected === 0 ? 'None' : product.boxSelected}</p>
                                                            </div>
                                                            <div className="product-info">
                                                                <p><strong>Frame:</strong> {product.name || 'N/A'} ({product.frameThickness ? `${product.frameThickness}"` : 'N/A'} thick)</p>
                                                                <p><strong>Image Dimensions:</strong> {product.imageDimensions?.width || 'N/A'}" × {product.imageDimensions?.height || 'N/A'}"</p>
                                                                <p><strong>Orientation:</strong> {product.frameOrientation ? product.frameOrientation.charAt(0).toUpperCase() + product.frameOrientation.slice(1) : 'Portrait'}</p>
                                                                <p><strong>Glass:</strong> {product.glassType || 'None picked'}</p>
                                                                <p><strong>Hanging:</strong> {product.hangingStyle || 'None picked'}</p>
                                                                <p><strong>Medium:</strong> {product.medium || 'N/A'}</p>
                                                                <p><strong>Stretching Style:</strong> {product.stretchingStyle || 'None'}</p>
                                                                <p><strong>Matboard 1:</strong> {product.matboardNum >= 1 && product.selectedMatboards && product.selectedMatboards[0] ? `${product.selectedMatboards[0].displayName} (${product.selectedMatboards[0].style}, ${product.matboardThickness || 'N/A'}" thick)` : 'None picked'}</p>
                                                                <p><strong>Matboard 2:</strong> {product.matboardNum >= 2 && product.selectedMatboards && product.selectedMatboards[1] ? `${product.selectedMatboards[1].displayName} (${product.selectedMatboards[1].style}, ${product.matboardThickness || 'N/A'}" thick)` : 'None picked'}</p>
                                                                <p><strong>Matboard 3:</strong> {product.matboardNum >= 3 && product.selectedMatboards && product.selectedMatboards[2] ? `${product.selectedMatboards[2].displayName} (${product.selectedMatboards[2].style}, ${product.matboardThickness || 'N/A'}" thick)` : 'None picked'}</p>
                                                                <p><strong>SKU:</strong> {product.selectedFrame || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* MIDDLE PANE: Shipping Labels */}
                                <div className="view-pane">
                                    <h3>Shipping Labels</h3>
                                    <div className="labels-scroll-container">
                                        {!combinedLabelUrl ? (
                                            <p>No labels found</p>
                                        ) : labelImages.length === 0 ? (
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>Loading label previews...</p>
                                        ) : (
                                            labelImages.map((imgSrc, idx) => (
                                                <div key={idx} className="label-preview-card">
                                                    <p style={{ fontWeight: 600, marginBottom: '8px' }}>Box {idx + 1} Label</p>
                                                    <img
                                                        src={imgSrc}
                                                        alt={`Box ${idx + 1} Label`}
                                                        style={{
                                                            width: '100%',
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: '4px',
                                                            background: '#fff'
                                                        }}
                                                    />
                                                </div>
                                            ))
                                        )}
                                    </div>


                                    {combinedLabelUrl && (
                                        <div className="label-actions-divider">
                                        <div className="label-actions">
                                            <div className="label-actions-section" style={{ justifyContent: 'flex-start' }}>
                                                <p className="label-actions-header">Box Label Range</p>
                                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', width: '100%' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                                                        <input
                                                            type="radio"
                                                            name="pageSelect"
                                                            value="all"
                                                            checked={pageSelectMode === 'all'}
                                                            onChange={() => { setPageSelectMode('all'); setPageRangeError(''); }}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        All
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                                                        <input
                                                            type="radio"
                                                            name="pageSelect"
                                                            value="range"
                                                            checked={pageSelectMode === 'range'}
                                                            onChange={() => setPageSelectMode('range')}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        Specific
                                                    </label>
                                                </div>
                                                {pageSelectMode === 'range' && (
                                                    <>
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. 1, 3-4, 6"
                                                            value={pageRangeInput}
                                                            onChange={(e) => {
                                                                setPageRangeInput(e.target.value);
                                                                setPageRangeError(validatePageRange(e.target.value));
                                                            }}
                                                            style={{
                                                                padding: '6px 8px',
                                                                border: `1px solid ${pageRangeError ? '#dc3545' : '#ccc'}`,
                                                                borderRadius: '5px',
                                                                fontSize: '12px',
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                marginTop: '2px',
                                                                textAlign: 'center'
                                                            }}
                                                        />
                                                        {pageRangeError && (
                                                            <span style={{ color: '#dc3545', fontSize: '11px', marginTop: '4px', display: 'block', textAlign: 'left' }}>
                                                                {pageRangeError}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className="label-actions-section">
                                                <p className="label-actions-header">Dymo LabelWriter</p>
                                                <button className="label-btn" onClick={() => {
                                                    if (pageSelectMode === 'range' && pageRangeError) {
                                                        alert('Please fix the Box Label Range error before printing.');
                                                        return;
                                                    }
                                                    handlePrintLabel(combinedLabelUrl);
                                                }}>
                                                    Print
                                                </button>
                                                <button className="label-btn" onClick={() => {
                                                    if (pageSelectMode === 'range' && pageRangeError) {
                                                        alert('Please fix the Box Label Range error before downloading.');
                                                        return;
                                                    }
                                                    handleDownloadLabel(combinedLabelUrl);
                                                }}>
                                                    Download
                                                </button>
                                                <span onClick={handleDownloadPrintServer} style={{ display: 'block', textAlign: 'center', fontSize: '12px', color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}>Print Server Download</span>
                                            </div>
                                            <div className="label-actions-section">
                                                <p className="label-actions-header">Regular Printers</p>
                                                <button className="label-btn laser-print-btn" onClick={() => {
                                                    if (pageSelectMode === 'range' && pageRangeError) {
                                                        alert('Please fix the Box Label Range error before printing.');
                                                        return;
                                                    }
                                                    handleLaserPrint();
                                                }}>
                                                    Print
                                                </button>
                                                <button className="label-btn" onClick={() => {
                                                    if (pageSelectMode === 'range' && pageRangeError) {
                                                        alert('Please fix the Box Label Range error before downloading.');
                                                        return;
                                                    }
                                                    handleDownloadLaserLabels();
                                                }}>
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                </div>

                                {/* RIGHT PANE: Box & Label Details */}
                                <div className="view-pane">
                                    <h3>Box & Label Details</h3>
                                    <div className="products-scroll-container">
                                        {finalizedBoxes.length === 0 ? (
                                            <p>No box details found</p>
                                        ) : (
                                            finalizedBoxes.map(box => (
                                                <div key={box.boxNumber} className="box-detail-card">
                                                    <h4>Box #{box.boxNumber}</h4>
                                                    <p><strong>Tracking #:</strong> {box.trackingNumber || 'N/A'}</p>
                                                    <p><strong>Box Dimensions:</strong> {
                                                        box.boxDimensions
                                                            ? `${box.boxDimensions.length}" L × ${box.boxDimensions.width}" W × ${box.boxDimensions.height}" H, ${box.boxDimensions.weight} lbs`
                                                            : 'N/A'
                                                    }</p>
                                                    <p><strong>Product numbers in box:</strong> {box.productsAssociated || 'None'}</p>
                                                    <p><strong>Label Generation Time (EST):</strong><br />{orderInfo?.labelGenerationTime || 'N/A'}</p>
                                                    <p><strong>Label Estimated Rate:</strong> {box.labelPrice || 'N/A'}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div className="label-total-price">
                                        <p><strong>Total Estimated Shipping Cost:</strong> {totalLabelPrice === 'N/A' ? 'N/A' : `$${totalLabelPrice}`}</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {showSettingsPopup && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '10px', padding: '30px',
                        maxWidth: '560px', width: '90%', maxHeight: '85vh', overflowY: 'auto',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif',
                        position: 'relative'
                    }}>
                        {/* Header row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>Table Preset Settings</h3>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button
                                    onClick={handleSettingsResetToDefaults}
                                    style={{ background: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}
                                >
                                    Reset Presets
                                </button>
                                <button
                                    onClick={() => {
                                        if (settingsSnapshot) {
                                            setSettingsFilterMode(settingsSnapshot.filterMode);
                                            setSettingsSortMode(settingsSnapshot.sortMode);
                                            setSettingsPageMode(settingsSnapshot.pageMode);
                                            setSettingsFilterValue(settingsSnapshot.filterValue);
                                            setSettingsFilterSearchItem(settingsSnapshot.filterSearchItem);
                                            setSettingsFilterSearchInput(settingsSnapshot.filterSearchInput);
                                            setSettingsFilterDateBegin(settingsSnapshot.filterDateBegin);
                                            setSettingsFilterDateEnd(settingsSnapshot.filterDateEnd);
                                            setSettingsSortValue(settingsSnapshot.sortValue);
                                            setSettingsSortDirection(settingsSnapshot.sortDirection);
                                            setSettingsPageValue(settingsSnapshot.pageValue);
                                        }
                                        setSettingsDateError('');
                                        setShowSettingsPopup(false);
                                    }}
                                    style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666', lineHeight: 1 }}
                                >×</button>
                            </div>
                        </div>

                        {/* FILTER section */}
                        <div style={{ marginBottom: '22px', borderBottom: '1px solid #eee', paddingBottom: '18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 600, fontSize: '14px', minWidth: '80px' }}>Filter</label>
                                <select
                                    value={settingsFilterMode}
                                    onChange={e => {
                                        setSettingsFilterMode(e.target.value);
                                        if (e.target.value === 'selectedHere') setTimeout(fetchSettingsPageOptions, 100);
                                    }}
                                    style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}
                                >
                                    <option value="lastPicked">Last Picked</option>
                                    <option value="selectedHere">Edit Preset</option>
                                    <option value="dontSave">Don't Save</option>
                                </select>
                            </div>
                            {settingsFilterMode === 'selectedHere' && (
                                <div style={{ marginLeft: '92px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <select
                                        value={settingsFilterValue}
                                        onChange={e => { setSettingsFilterValue(e.target.value); setTimeout(fetchSettingsPageOptions, 100); }}
                                        style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', maxWidth: '220px' }}
                                    >
                                        <option value="all">All</option>
                                        <option value="unfinished">Unfinished</option>
                                        <option value="finished">Finished</option>
                                        <option value="dateRange">Date Range</option>
                                        <option value="search">Search</option>
                                    </select>
                                    {settingsFilterValue === 'dateRange' && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <input type="date" value={settingsFilterDateBegin}
                                                    onChange={e => { setSettingsFilterDateBegin(e.target.value); setSettingsDateError(''); setTimeout(fetchSettingsPageOptions, 100); }}
                                                    style={{ padding: '6px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }} />
                                                <span>to</span>
                                                <input type="date" value={settingsFilterDateEnd}
                                                    onChange={e => { setSettingsFilterDateEnd(e.target.value); setSettingsDateError(''); setTimeout(fetchSettingsPageOptions, 100); }}
                                                    style={{ padding: '6px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }} />
                                            </div>
                                            {settingsDateError && (
                                                <span style={{ color: '#dc3545', fontSize: '12px' }}>{settingsDateError}</span>
                                            )}
                                        </div>
                                    )}
                                    {settingsFilterValue === 'search' && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <select
                                                value={settingsFilterSearchItem}
                                                onChange={e => { setSettingsFilterSearchItem(e.target.value); setTimeout(fetchSettingsPageOptions, 100); }}
                                                style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', maxWidth: '220px' }}
                                            >
                                                <option value="all-fields">All Fields</option>
                                                <option value="status">Status</option>
                                                <option value="orderID">Order ID</option>
                                                <option value="name">Name</option>
                                                <option value="email">Email</option>
                                                <option value="address">Address</option>
                                                <option value="productCount">Product Count</option>
                                                <option value="totalPrice">Total Price</option>
                                                <option value="purchaseDate">Purchase Date</option>
                                            </select>
                                            <input type="text" placeholder="Search term..." value={settingsFilterSearchInput}
                                                onChange={e => { setSettingsFilterSearchInput(e.target.value); setTimeout(fetchSettingsPageOptions, 300); }}
                                                style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', maxWidth: '280px' }} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* SORT section */}
                        <div style={{ marginBottom: '22px', borderBottom: '1px solid #eee', paddingBottom: '18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 600, fontSize: '14px', minWidth: '80px' }}>Sort</label>
                                <select
                                    value={settingsSortMode}
                                    onChange={e => setSettingsSortMode(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}
                                >
                                    <option value="lastPicked">Last Picked</option>
                                    <option value="selectedHere">Edit Preset</option>
                                    <option value="dontSave">Don't Save</option>
                                </select>
                            </div>
                            {settingsSortMode === 'selectedHere' && (
                                <div style={{ marginLeft: '92px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <select
                                        value={settingsSortValue}
                                        onChange={e => { setSettingsSortValue(e.target.value); setTimeout(fetchSettingsPageOptions, 100); }}
                                        style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', maxWidth: '220px' }}
                                    >
                                        <option value="purchaseDate">Purchase Date</option>
                                        <option value="status">Status</option>
                                        <option value="orderID">Order ID</option>
                                        <option value="name">Name</option>
                                        <option value="email">Email</option>
                                        <option value="address">Address</option>
                                        <option value="productCount">Products</option>
                                        <option value="totalPrice">Total</option>
                                    </select>
                                    <select
                                        value={settingsSortDirection}
                                        onChange={e => setSettingsSortDirection(e.target.value)}
                                        style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', maxWidth: '220px' }}
                                    >
                                        <option value="asc">Ascending</option>
                                        <option value="desc">Descending</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* PAGE LOCK section */}
                        <div style={{ marginBottom: '22px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 600, fontSize: '14px', minWidth: '80px' }}>Page Lock</label>
                                <select
                                    value={settingsPageMode}
                                    onChange={e => { setSettingsPageMode(e.target.value); if (e.target.value === 'selectedHere') fetchSettingsPageOptions(); }}
                                    style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}
                                >
                                    <option value="lastPicked">Last Picked</option>
                                    <option value="selectedHere">Edit Preset</option>
                                    <option value="dontSave">Don't Save</option>
                                </select>
                            </div>
                            {settingsPageMode === 'selectedHere' && (
                                <div style={{ marginLeft: '92px' }}>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settingsPageValue}
                                        onChange={e => setSettingsPageValue(Math.max(1, parseInt(e.target.value) || 1))}
                                        style={{ padding: '6px 10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', width: '100px' }}
                                        placeholder="Page #"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer buttons */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    if (settingsSnapshot) {
                                        setSettingsFilterMode(settingsSnapshot.filterMode);
                                        setSettingsSortMode(settingsSnapshot.sortMode);
                                        setSettingsPageMode(settingsSnapshot.pageMode);
                                        setSettingsFilterValue(settingsSnapshot.filterValue);
                                        setSettingsFilterSearchItem(settingsSnapshot.filterSearchItem);
                                        setSettingsFilterSearchInput(settingsSnapshot.filterSearchInput);
                                        setSettingsFilterDateBegin(settingsSnapshot.filterDateBegin);
                                        setSettingsFilterDateEnd(settingsSnapshot.filterDateEnd);
                                        setSettingsSortValue(settingsSnapshot.sortValue);
                                        setSettingsSortDirection(settingsSnapshot.sortDirection);
                                        setSettingsPageValue(settingsSnapshot.pageValue);
                                    }
                                    setSettingsDateError('');
                                    setShowSettingsPopup(false);
                                }}
                                style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSettingsSave}
                                disabled={isSettingsSaveDisabled() || settingsSaving}
                                style={{
                                    padding: '10px 20px',
                                    background: isSettingsSaveDisabled() || settingsSaving ? '#ccc' : '#ff4172',
                                    color: 'white', border: 'none', borderRadius: '5px',
                                    cursor: isSettingsSaveDisabled() || settingsSaving ? 'not-allowed' : 'pointer',
                                    fontSize: '14px', fontWeight: 600
                                }}
                            >
                                {settingsSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {printServerPopup && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '30px',
                        maxWidth: '420px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        fontFamily: 'Arial, sans-serif',
                        position: 'relative'
                    }}>
                        <button
                            onClick={() => setPrintServerPopup(false)}
                            style={{
                                position: 'absolute',
                                top: '12px',
                                right: '16px',
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer',
                                color: '#666'
                            }}
                        >
                            ×
                        </button>
                        <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '18px' }}>
                            Could not connect to the print server.
                        </h3>
                        <p style={{ margin: '0 0 20px 0', color: '#555', fontSize: '14px', lineHeight: '1.6' }}>
                            Have you installed the print server? If not{' '}
                            <span
                                onClick={handleDownloadPrintServer}
                                style={{ color: '#ff4172', cursor: 'pointer', textDecoration: 'underline', fontWeight: '600' }}
                            >
                                click here
                            </span>
                            {' '}to download.
                            <br /><br />
                            Once downloaded, unzip/extract the folder and open the <strong>Readme.pdf</strong> file.
                        </p>
                        <button
                            onClick={() => setPrintServerPopup(false)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                backgroundColor: '#ff4172',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DimensionRequests;